import React, { useEffect, useMemo, useCallback, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import SATooltip from './SATooltip';
import ChatToggleButton from '../agents/ChatToggleButton';
import WorkflowHistoryDrawer from '../Workflow/WorkflowHistoryDrawer';
import { API_BASE_URL, baseDomain, statusMap } from '../../settings/shared/constants';
import { showNotificationGlobal } from './NotificationProvider';
import logo_icon from '../../assets/img/logo_icon.png';
import WorkflowService from '../../services/api/workflow';
import EvaluateModal from '../evaluation/EvaluateModal';
import SAModalConfirm from './SAModalConfirm';
import { sxIcons } from '../../settings/shared/sxIcons';

// Security: Sanitize user input to prevent XSS attacks
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  // Remove all HTML tags and attributes, only allow plain text
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
};

const normalizeProviderKey = (v) => {
  const s = sanitizeText(String(v || '')).trim().toLowerCase();
  if (!s) return '';
  // common normalizations (e.g. open_api -> openapi)
  const compact = s.replace(/[^a-z0-9]/g, '');
  return compact || s;
};

const getProviderIconEl = (provider) => {
  const raw = sanitizeText(String(provider || '')).trim().toLowerCase();
  const compact = normalizeProviderKey(raw);
  const icon = sxIcons?.[raw] || sxIcons?.[compact];
  if (!icon) return null;
  return (
      <span
          style={{
            width: 18,
            height: 18,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            overflow: 'hidden',
          }}
      >
      <span style={{ transform: 'scale(0.6)', transformOrigin: 'center' }}>{icon}</span>
    </span>
  );
};

// Constants for Alert styles (moved outside component to prevent recreation)
const ALERT_STYLES = {
  info: {
    bg: "#e0f2fe",
    iconBg: "#bae6fd",
    icon: "fa-play",
  },
  success: {
    bg: "#dcfce7",
    iconBg: "#bbf7d0",
    icon: "fa-check",
  },
  warning: {
    bg: "#fef9c3",
    iconBg: "#fde68a",
    icon: "fa-clock",
  },
  error: {
    bg: "#fee2e2",
    iconBg: "#fecaca",
    icon: "fa-exclamation-triangle",
  }
};


// Memoized Alert Component
const Alert = React.memo(({ type = "info", title, subtitle, onClose, infoTooltip }) => {
  const s = ALERT_STYLES[type] || ALERT_STYLES.info;

  // Memoize styles to prevent recreation
  const containerStyle = useMemo(() => ({
    display: "flex",
    alignItems: "center",
    padding: "7px 7px",
    borderRadius: "12px",
    background: s.bg,
    width: infoTooltip ? "auto" : "230px",
    justifyContent: "space-between",
    gap: "8px",
  }), [s.bg, infoTooltip]);

  const innerContainerStyle = useMemo(() => ({
    display: "flex",
    gap: "12px",
    alignItems: "center",
    flex: 1,
  }), []);

  const iconCircleStyle = useMemo(() => ({
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    background: s.iconBg,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
  }), [s.iconBg]);

  const titleStyle = useMemo(() => ({
    fontWeight: 600,
    fontSize: "15px",
  }), []);

  const subtitleStyle = useMemo(() => ({
    fontSize: "13px",
    opacity: 0.7,
  }), []);

  // Sanitize title and subtitle
  const sanitizedTitle = useMemo(() => sanitizeText(title), [title]);
  const sanitizedSubtitle = useMemo(() => subtitle ? sanitizeText(subtitle) : null, [subtitle]);

  return (
      <div style={containerStyle}>
        <div style={innerContainerStyle}>
          <div style={iconCircleStyle}>
            <i className={`fa-solid ${s.icon}`} style={{ color: "#000", fontSize: "12px" }}></i>
          </div>
          <div style={{ flex: 1 }}>
            <div style={titleStyle}>{sanitizedTitle}</div>
            {sanitizedSubtitle && <div style={subtitleStyle}>{sanitizedSubtitle}</div>}
          </div>
        </div>
        {infoTooltip && (
            <SATooltip
                title={infoTooltip}
                placement="bottom"
                bg="#1f2937"
                color="#ffffff"
                maxWidth="800px"
                fontFamily="inherit"
            >
              <i
                  className="fa-solid fa-circle-info"
                  style={{
                    fontSize: '18px',
                    opacity: 0.8,
                    cursor: 'pointer',
                    color: s.iconBg,
                    flexShrink: 0
                  }}
              ></i>
            </SATooltip>
        )}
      </div>
  );
});

Alert.displayName = 'Alert';

// Helper function to map event types to display status
const getDisplayStatus = (eventType) => {
  return statusMap[eventType] || eventType;
};

const copyTextToClipboard = async (text) => {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    // fall back
  }

  try {
    const el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', '');
    el.style.position = 'fixed';
    el.style.top = '-1000px';
    el.style.left = '-1000px';
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return Boolean(ok);
  } catch (e) {
    return false;
  }
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const formatTimestamp = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(d);
  } catch (e) {
    // Fallback if Intl fails for any reason
    return d.toLocaleString?.() || String(iso);
  }
};

const renderCurlHighlighted = (curlText) => {
  const lines = String(curlText || '').split('\n');
  const base = {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontSize: 11,
    lineHeight: 1.75,
  };

  const c = {
    plain: { color: '#e2e8f0' },
    keyword: { color: '#38bdf8', fontWeight: 900 },
    url: { color: '#86efac' },
    flag: { color: '#a5b4fc' },
    header: { color: '#c4b5fd' },
    jsonKey: { color: '#93c5fd' },
    jsonStr: { color: '#f9a8d4' },
    punct: { color: '#94a3b8' },
  };

  const renderLine = (line, i) => {
    const l = String(line || '');
    const parts = [];

    // curl command
    if (l.startsWith('curl')) {
      parts.push(
          <span key={`${i}-curl`} style={c.keyword}>
          curl
        </span>
      );
      parts.push(<span key={`${i}-rest`} style={c.plain}>{l.slice(4)}</span>);
      return <span key={`l-${i}`}>{parts}</span>;
    }

    // flags and headers
    if (l.trimStart().startsWith('-H')) {
      const idx = l.indexOf('-H');
      parts.push(<span key={`${i}-pre`} style={c.plain}>{l.slice(0, idx)}</span>);
      parts.push(<span key={`${i}-flag`} style={c.flag}>-H</span>);
      const rest = l.slice(idx + 2);
      // highlight header name up to colon
      const m = rest.match(/^\s*'([^']+)'/);
      if (m?.[1]) {
        const headerStr = m[1];
        const colonIdx = headerStr.indexOf(':');
        if (colonIdx !== -1) {
          parts.push(<span key={`${i}-q1`} style={c.plain}>'</span>);
          parts.push(<span key={`${i}-hn`} style={c.header}>{headerStr.slice(0, colonIdx + 1)}</span>);
          parts.push(<span key={`${i}-hv`} style={c.plain}>{headerStr.slice(colonIdx + 1)}</span>);
          parts.push(<span key={`${i}-q2`} style={c.plain}>'</span>);
          parts.push(<span key={`${i}-tail`} style={c.plain}>{rest.slice(m[0].length)}</span>);
          return <span key={`l-${i}`}>{parts}</span>;
        }
      }
      parts.push(<span key={`${i}-rest2`} style={c.plain}>{rest}</span>);
      return <span key={`l-${i}`}>{parts}</span>;
    }

    // URL line
    if (l.includes('http') && l.includes('/api/')) {
      const q1 = l.indexOf("'");
      const q2 = l.indexOf("'", q1 + 1);
      if (q1 !== -1 && q2 !== -1) {
        parts.push(<span key={`${i}-a`} style={c.plain}>{l.slice(0, q1 + 1)}</span>);
        parts.push(<span key={`${i}-url`} style={c.url}>{l.slice(q1 + 1, q2)}</span>);
        parts.push(<span key={`${i}-b`} style={c.plain}>{l.slice(q2)}</span>);
        return <span key={`l-${i}`}>{parts}</span>;
      }
    }

    // JSON-ish lines
    if (l.includes('"') && (l.includes('{') || l.includes('}') || l.includes(':'))) {
      const keyMatch = l.match(/^(\s*)\"([^\"]+)\"(\s*:\s*)(.*)$/);
      if (keyMatch) {
        const [, ws, key, colon, rest] = keyMatch;
        parts.push(<span key={`${i}-ws`} style={c.punct}>{ws}</span>);
        parts.push(<span key={`${i}-qk1`} style={c.punct}>"</span>);
        parts.push(<span key={`${i}-k`} style={c.jsonKey}>{key}</span>);
        parts.push(<span key={`${i}-qk2`} style={c.punct}>"</span>);
        parts.push(<span key={`${i}-colon`} style={c.punct}>{colon}</span>);
        const strMatch = rest.match(/^\"([^\"]*)\"(.*)$/);
        if (strMatch) {
          parts.push(<span key={`${i}-qs1`} style={c.punct}>"</span>);
          parts.push(<span key={`${i}-sv`} style={c.jsonStr}>{strMatch[1]}</span>);
          parts.push(<span key={`${i}-qs2`} style={c.punct}>"</span>);
          parts.push(<span key={`${i}-trail`} style={c.punct}>{strMatch[2]}</span>);
        } else {
          parts.push(<span key={`${i}-rest`} style={c.plain}>{rest}</span>);
        }
        return <span key={`l-${i}`}>{parts}</span>;
      }
    }

    return (
        <span key={`l-${i}`} style={c.plain}>
        {l}
      </span>
    );
  };

  return (
      <code style={base}>
        {lines.map((line, i) => (
            <React.Fragment key={`f-${i}`}>
              {renderLine(line, i)}
              {i < lines.length - 1 ? '\n' : null}
            </React.Fragment>
        ))}
      </code>
  );
};

/** Goal / role text in published-history app config: expandable + spaced for readability */
const PublishedHistoryAgentGoalRole = React.memo(({ goal, role }) => {
  const [goalExpanded, setGoalExpanded] = useState(false);
  const [roleExpanded, setRoleExpanded] = useState(false);

  const goalNeedsToggle = Boolean(goal && (goal.length > 160 || goal.split('\n').length > 3));
  const roleNeedsToggle = Boolean(role && (role.length > 160 || role.split('\n').length > 3));

  const blockStyle = (expanded, needsToggle) => ({
    fontSize: 11,
    fontWeight: 800,
    color: '#64748b',
    lineHeight: 1.55,
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    ...(expanded || !needsToggle
        ? {}
        : {
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
        }),
  });

  const labelStyle = {
    display: 'block',
    fontWeight: 950,
    color: '#334155',
    fontSize: 11,
    letterSpacing: '0.01em',
    marginBottom: 6,
  };

  const toggleBtnStyle = {
    marginTop: 8,
    padding: 0,
    border: 'none',
    background: 'transparent',
    color: '#0284c7',
    fontSize: 11,
    fontWeight: 900,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  };

  if (!goal && !role) return null;

  return (
      <div
          style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: '1px solid rgba(15,23,42,0.08)',
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
      >
        {goal ? (
            <div>
              <span style={labelStyle}>Goal</span>
              <div style={blockStyle(goalExpanded, goalNeedsToggle)} title={goalExpanded ? undefined : goal}>
                {goal}
              </div>
              {goalNeedsToggle ? (
                  <button
                      type="button"
                      onClick={() => setGoalExpanded((v) => !v)}
                      style={toggleBtnStyle}
                      aria-expanded={goalExpanded}
                  >
                    {goalExpanded ? 'Show less' : 'Show more'}
                    <i className={`fa-solid fa-chevron-${goalExpanded ? 'up' : 'down'}`} style={{ fontSize: 9, opacity: 0.85 }} />
                  </button>
              ) : null}
            </div>
        ) : null}
        {role ? (
            <div>
              <span style={labelStyle}>Role</span>
              <div style={blockStyle(roleExpanded, roleNeedsToggle)} title={roleExpanded ? undefined : role}>
                {role}
              </div>
              {roleNeedsToggle ? (
                  <button
                      type="button"
                      onClick={() => setRoleExpanded((v) => !v)}
                      style={toggleBtnStyle}
                      aria-expanded={roleExpanded}
                  >
                    {roleExpanded ? 'Show less' : 'Show more'}
                    <i className={`fa-solid fa-chevron-${roleExpanded ? 'up' : 'down'}`} style={{ fontSize: 9, opacity: 0.85 }} />
                  </button>
              ) : null}
            </div>
        ) : null}
      </div>
  );
});
PublishedHistoryAgentGoalRole.displayName = 'PublishedHistoryAgentGoalRole';

// WorkflowHeader Component
const WorkflowHeader = ({
                          projectName = 'Untitled Project',
                          agentNodes = [],
                          isWorkflowRunning = false,
                          hasAgents = false,
                          hasTasks = false,
                          hasInputs = false,
                          activeButton = null,
                          workflowEventStatus = null,
                          workflowError = null,
                          workflowResult = null,
                          isWorkflowSaved = false,
                          projectId = null,
                          orchestratorId = null,
                          historyData = null,
                          onSetActiveButton,
                          onExecute,
                          onGenerate,
                          onSave,
                          onPublish,
                          onClear,
                          onConsole,
                          onClose,
                          showProjectInfo = true,
                          isChatVisible = false,
                          onToggleChat = null
                        }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isHistoryDrawerOpen, setIsHistoryDrawerOpen] = useState(false);
  const [waitingStatus, setWaitingStatus] = useState(null);
  const [podControlStatus, setPodControlStatus] = useState(null);
  const [isMessageEventControl, setIsMessageEventControl] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isPublishConfirmOpen, setIsPublishConfirmOpen] = useState(false);
  const [isPublishConfirmSplitMenuOpen, setIsPublishConfirmSplitMenuOpen] = useState(false);
  const [publishName, setPublishName] = useState('');
  const [publishDescription, setPublishDescription] = useState('');
  const [publishError, setPublishError] = useState(null);

  const [isPublishedHistoryOpen, setIsPublishedHistoryOpen] = useState(false);
  const [publishedHistoryLoading, setPublishedHistoryLoading] = useState(false);
  const [publishedHistoryError, setPublishedHistoryError] = useState(null);
  const [publishedHistoryPage, setPublishedHistoryPage] = useState(0);
  const [publishedHistorySize] = useState(10);
  const [publishedHistory, setPublishedHistory] = useState({ content: [], totalPages: 0, totalElements: 0 });
  const [expandedPublishedCurlId, setExpandedPublishedCurlId] = useState(null);
  /** Which tab is active per publish id: 'details' | 'appInfo' */
  const [publishedHistoryDetailTab, setPublishedHistoryDetailTab] = useState({});
  const [hoveredPublishedCurlId, setHoveredPublishedCurlId] = useState(null);
  const [publishedHistoryHasMore, setPublishedHistoryHasMore] = useState(true);
  const publishedHistoryScrollRef = useRef(null);
  const publishedHistoryLoadMoreRef = useRef(null);
  const lastObservedPublishedPageRef = useRef(-1);
  const [isPublishedHistoryEndVisible, setIsPublishedHistoryEndVisible] = useState(false);
  const [restoringPublishedId, setRestoringPublishedId] = useState(null);
  const [hoveredPublishedHistoryId, setHoveredPublishedHistoryId] = useState(null);
  const [isStopConfirmOpen, setIsStopConfirmOpen] = useState(false);
  const [isRestoreConfirmOpen, setIsRestoreConfirmOpen] = useState(false);
  const [restoreConfirmData, setRestoreConfirmData] = useState(null); // { id, title, description }
  // Evaluate modal state
  const [isEvaluateOpen, setIsEvaluateOpen] = useState(false);

  // Sanitize project name
  const sanitizedProjectName = useMemo(() => {
    return sanitizeText(projectName) || 'Untitled Project';
  }, [projectName]);

  // Listen for ConsoleDrawer "waiting for response" updates
  useEffect(() => {
    const handler = (event) => {
      const message = event?.detail?.message ?? null;
      setWaitingStatus(message);
    };
    window.addEventListener('workflow-waiting-status', handler);
    return () => window.removeEventListener('workflow-waiting-status', handler);
  }, []);

  useEffect(() => {
    const handler = (event) => {
      setPodControlStatus(event?.detail?.status || null);
      if (typeof event?.detail?.messageEvent === 'boolean') {
        setIsMessageEventControl(event.detail.messageEvent);
      }
    };
    window.addEventListener('workflow-pod-status', handler);
    return () => window.removeEventListener('workflow-pod-status', handler);
  }, []);

  // Stop is added only for a running Message Event (Kafka, RabbitMQ, MQTT, and the rest).
  // Every other event keeps the original Execute button.
  const isStoppingPod = isMessageEventControl && podControlStatus === 'Stopping';
  const canStopPod = isMessageEventControl && podControlStatus === 'Running';
  const executeDisabled = isStoppingPod
      || (!canStopPod && ((!hasAgents && !hasTasks) || isWorkflowRunning || !projectId));
  const executeLabel = isStoppingPod ? 'Stopping' : canStopPod ? 'Stop' : 'Execute';
  // Message Event execution keeps Save and Publish available. Other running
  // workflows still disable them.
  const messageEventExecution = isMessageEventControl
      && (podControlStatus === 'Running' || podControlStatus === 'Stopping');
  const savePublishLocked = isWorkflowRunning && !messageEventExecution;

  // Close "More" dropdown on outside click / escape
  useEffect(() => {
    if (!isMoreOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsMoreOpen(false);
    };
    const onMouseDown = (e) => {
      if (!e.target.closest?.('.sa-workflow-header-more')) setIsMoreOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [isMoreOpen]);

  // Close publish drawer on escape
  useEffect(() => {
    if (!isPublishConfirmOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsPublishConfirmOpen(false);
        setIsPublishConfirmSplitMenuOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isPublishConfirmOpen]);

  // Close publish confirm split-menu on outside click / escape
  useEffect(() => {
    if (!isPublishConfirmSplitMenuOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsPublishConfirmSplitMenuOpen(false);
    };
    const onMouseDown = (e) => {
      if (!e.target.closest?.('.sa-publish-confirm-split')) setIsPublishConfirmSplitMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [isPublishConfirmSplitMenuOpen]);

  // Close restore confirmation modal on escape
  useEffect(() => {
    if (!isRestoreConfirmOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsRestoreConfirmOpen(false);
        setRestoreConfirmData(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isRestoreConfirmOpen]);

  // Close published history drawer on escape
  useEffect(() => {
    if (!isPublishedHistoryOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsPublishedHistoryOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isPublishedHistoryOpen]);

  const fetchPublishedHistory = useCallback(async (page = 0, { append = false } = {}) => {
    if (!projectId) {
      setPublishedHistoryError('Missing Project id.');
      return;
    }
    setPublishedHistoryLoading(true);
    setPublishedHistoryError(null);
    try {
      const res = await WorkflowService.getOrchestratorPublishedHistory(projectId, page, publishedHistorySize);
      const dtoPage = res?.orchestratorDTOS || res?.orchestratorDTOPage || res?.data?.orchestratorDTOS;
      const content = dtoPage?.content || [];
      const totalPages = dtoPage?.totalPages ?? 0;
      const totalElements = dtoPage?.totalElements ?? content.length;
      setPublishedHistory((prev) => {
        if (!append || page <= 0) return { content, totalPages, totalElements };
        const prevContent = prev?.content || [];
        const merged = [...prevContent, ...content];
        // De-dupe by id (defensive against overlapping pages)
        const seen = new Set();
        const deduped = [];
        for (const it of merged) {
          const k = it?.id != null ? String(it.id) : JSON.stringify(it);
          if (seen.has(k)) continue;
          seen.add(k);
          deduped.push(it);
        }
        return { content: deduped, totalPages, totalElements };
      });
      setPublishedHistoryPage(page);
      if (totalPages > 0) setPublishedHistoryHasMore(page < totalPages - 1);
      else setPublishedHistoryHasMore(content.length >= publishedHistorySize);
    } catch (e) {
      setPublishedHistoryError(e?.message || 'Failed to load published history.');
    } finally {
      setPublishedHistoryLoading(false);
    }
  }, [projectId, publishedHistorySize]);

  // Smooth infinite-scroll: IntersectionObserver sentinel for "load more"
  useEffect(() => {
    if (!isPublishedHistoryOpen) return;
    if (!publishedHistoryHasMore) return;
    if (publishedHistoryLoading) return;

    const rootEl = publishedHistoryScrollRef.current;
    const target = publishedHistoryLoadMoreRef.current;
    if (!rootEl || !target) return;

    // Avoid immediately re-triggering for the same page.
    // (IntersectionObserver can fire multiple times for the same sentinel visibility.)
    if (lastObservedPublishedPageRef.current === publishedHistoryPage) return;

    const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries?.[0];
          setIsPublishedHistoryEndVisible(Boolean(entry?.isIntersecting));
          if (!entry?.isIntersecting) return;
          if (publishedHistoryLoading) return;
          if (!publishedHistoryHasMore) return;

          // Mark and request next page
          lastObservedPublishedPageRef.current = publishedHistoryPage;
          fetchPublishedHistory(publishedHistoryPage + 1, { append: true });
        },
        {
          root: rootEl,
          rootMargin: '280px 0px 280px 0px',
          threshold: 0.01,
        }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [
    isPublishedHistoryOpen,
    publishedHistoryHasMore,
    publishedHistoryLoading,
    publishedHistoryPage,
    fetchPublishedHistory,
  ]);

  // Reset end-visible state when drawer closes
  useEffect(() => {
    if (isPublishedHistoryOpen) return;
    setIsPublishedHistoryEndVisible(false);
  }, [isPublishedHistoryOpen]);

  // Close published history drawer on escape
  useEffect(() => {
    if (isPublishedHistoryOpen) return;
    setIsPublishedHistoryEndVisible(false);
  }, [isPublishedHistoryOpen]);

  const handleEvaluateClick = useCallback(() => {
    setIsEvaluateOpen(true);
  }, []);

  const restorePublishedAsDefault = useCallback(
      async (publishedOrchestratorId) => {
        const safePublishedId = publishedOrchestratorId ? String(publishedOrchestratorId).trim() : '';
        if (!safePublishedId) return;

        try {
          setRestoringPublishedId(safePublishedId);

          const res = await WorkflowService.restoreOrchestrator(safePublishedId, orchestratorId);

          const isSuccess =
              res?.status === 'SUCCESS' ||
              res?.data?.status === 'SUCCESS' ||
              (!res?.status && !res?.errorMessage);

          if (!isSuccess) {
            throw new Error(
                res?.errorMessage || res?.message || 'Failed to restore default template'
            );
          }

          showNotificationGlobal(
              'Template restored successfully',
              'success',
              'bottomRight'
          );

          // Tell canvas to reload nodes/edges (DrawWorkFlow listens for this)
          window.dispatchEvent(
              new CustomEvent('workflow-restored-default', {
                detail: { restoredPublishedOrchestratorId: safePublishedId, res },
              })
          );

          await fetchPublishedHistory(0, { append: false });
        } catch (error) {
          showNotificationGlobal(
              error?.message || 'Failed to restore default template',
              'error',
              'bottomRight'
          );
        } finally {
          setRestoringPublishedId(null);
        }
      },
      [orchestratorId, fetchPublishedHistory]
  );

  const buildPublishedHistoryCurl = useCallback((publishedOrchestratorId) => {
    const host = API_BASE_URL;
    const safeId = publishedOrchestratorId ? String(publishedOrchestratorId) : '';
    const url = `${host}/api/project/orchestrator/execute/${encodeURIComponent(safeId)}`;
    const profile = JSON.parse(localStorage.getItem('profile'));
    const apiToken = profile?.apiToken || '';

    return [
      `curl -X 'POST' \\`,
      `  '${url}' \\`,
      `  -H 'accept: */*' \\`,
      `  -H 'api-token: ${apiToken}' \\`,
      `  -H 'Content-Type: application/json' \\`,
      `  -d '{`,
      `  "query": ""`,
      `}'`,
    ].join('\n');
  }, []);

  const iconBtnBaseStyle = useMemo(
      () => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '30px',
        minWidth: '30px',
        padding: '0 8px',
        borderRadius: '8px',
        backgroundColor: '#ffffff',
        transition: 'all 0.2s ease',
        border: '1px solid #e2e8f0',
        cursor: 'pointer',
      }),
      []
  );

  const shouldHideCloseButton = useMemo(() => {
    const path = location?.pathname || '';
    return path === '/project' || path.startsWith('/project/');
  }, [location?.pathname]);

  // Memoized header container style
  const headerStyle = useMemo(() => ({
    position: 'sticky',
    top: 0,
    zIndex: 1030,
    padding: '4px 10px',
    background: 'linear-gradient(to right, #ffffff, #f8f9fa)',
    borderBottom: '1px solid #e9ecef',
    boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px'
  }), []);

  // Memoized close handler
  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    } else {
      navigate('/apps');
    }
  }, [onClose, navigate]);

  const handlePublishClick = useCallback(() => {
    if (!onPublish) return;
    if ((!hasAgents && !hasTasks) || savePublishLocked || !projectId) return;
    setPublishName('');
    setPublishDescription('');
    setPublishError(null);
    setIsPublishConfirmOpen(true);
  }, [onPublish, hasAgents, hasTasks, savePublishLocked, projectId, sanitizedProjectName]);

  const handlePublishConfirm = useCallback(() => {
    const name = (publishName || '').trim();
    const description = (publishDescription || '').trim();

    if (!name) {
      setPublishError('Please enter a workflow name.');
      return;
    }

    setIsPublishConfirmOpen(false);
    setIsPublishConfirmSplitMenuOpen(false);
    setPublishError(null);
    onPublish?.({ name, description });
  }, [onPublish, publishName, publishDescription]);

  const handleSaveAndPublishConfirm = useCallback(async () => {
    const name = (publishName || '').trim();
    const description = (publishDescription || '').trim();

    if (!name) {
      setPublishError('Please enter a workflow name.');
      return;
    }

    try {
      await Promise.resolve(onSave?.({ silent: true }));
    } catch (e) {
      showNotificationGlobal(e?.message || 'Failed to save workflow.', 'error', 'bottomRight');
      return;
    }

    setIsPublishConfirmOpen(false);
    setIsPublishConfirmSplitMenuOpen(false);
    setPublishError(null);
    onPublish?.({ name, description });
  }, [onPublish, onSave, publishName, publishDescription]);

  // Memoized button hover handlers to prevent recreation
  const createButtonHoverHandlers = useCallback((config) => {
    return {
      onMouseEnter: (e) => {
        if (config.disabled) return;
        const target = e.currentTarget;
        if (config.hoverStyles) {
          Object.assign(target.style, config.hoverStyles);
        }
        if (config.iconSelector && config.iconHoverColor) {
          const icon = target.querySelector(config.iconSelector);
          if (icon) icon.style.color = config.iconHoverColor;
        }
      },
      onMouseLeave: (e) => {
        if (config.disabled) return;
        const target = e.currentTarget;
        if (config.defaultStyles) {
          Object.assign(target.style, config.defaultStyles);
        }
        if (config.iconSelector && config.iconDefaultColor) {
          const icon = target.querySelector(config.iconSelector);
          if (icon) icon.style.color = config.iconDefaultColor;
        }
      },
    };
  }, []);

  // Execute button handlers
  const executeButtonHandlers = useMemo(() => {
    if (executeDisabled) {
      return {};
    }
    if (canStopPod) {
      return createButtonHoverHandlers({
        disabled: false,
        hoverStyles: {
          borderColor: '#b91c1c',
          color: '#b91c1c',
        },
        defaultStyles: {
          background: '#ffffff',
          borderColor: '#dc2626',
          color: '#dc2626',
        },
      });
    }
    return createButtonHoverHandlers({
      disabled: false,
      hoverStyles: {
        borderColor: '#16a34a',
        color: '#16a34a',
      },
      defaultStyles: {
        background: '#ffffff',
        borderColor: 'rgb(25, 135, 84)',
        color: 'rgb(25, 135, 84)',
      },
    });
  }, [executeDisabled, canStopPod, createButtonHoverHandlers]);



  // Generate button handlers
  const generateButtonHandlers = useMemo(() => {
    if ((!hasAgents && !hasTasks) || isWorkflowRunning || !onGenerate) {
      return {};
    }
    return createButtonHoverHandlers({
      disabled: (!hasAgents && !hasTasks) || isWorkflowRunning || !onGenerate,
      hoverStyles: {
        borderColor: '#9333ea',
        color: '#9333ea',
      },
      defaultStyles: {
        background: '#ffffff',
        borderColor: '#a855f7',
        color: '#a855f7',
      },
    });
  }, [hasAgents, hasTasks, isWorkflowRunning, onGenerate, createButtonHoverHandlers]);

  // Save button handlers
  const saveButtonHandlers = useMemo(() => {
    if ((!hasAgents && !hasTasks) || savePublishLocked) {
      return {};
    }
    return createButtonHoverHandlers({
      disabled: savePublishLocked,
      hoverStyles: {
        borderColor: '#2563eb',
        color: '#2563eb',
      },
      defaultStyles: {
        background: '#ffffff',
        borderColor: '#3b82f6',
        color: '#3b82f6',
      },
    });
  }, [hasAgents, hasTasks, savePublishLocked, createButtonHoverHandlers]);

  // Publish button handlers
  const publishButtonHandlers = useMemo(() => {
    if ((!hasAgents && !hasTasks) || savePublishLocked || !onPublish || !projectId) {
      return {};
    }
    return createButtonHoverHandlers({
      disabled: (!hasAgents && !hasTasks) || savePublishLocked || !onPublish || !projectId,
      hoverStyles: {
        borderColor: '#0ea5e9',
        color: '#0ea5e9',
      },
      defaultStyles: {
        background: '#ffffff',
        borderColor: '#38bdf8',
        color: '#38bdf8',
      },
      iconSelector: 'i',
      iconHoverColor: '#0ea5e9',
      iconDefaultColor: '#38bdf8',
    });
  }, [hasAgents, hasTasks, savePublishLocked, onPublish, projectId, createButtonHoverHandlers]);

  // History button handlers
  const historyButtonHandlers = useMemo(() => {
    return createButtonHoverHandlers({
      disabled: false,
      hoverStyles: {
        borderColor: '#f49408',
        color: '#f49408',
      },
      defaultStyles: {
        background: '#ffffff',
        borderColor: '#f49408',
        color: '#f49408',
      },
    });
  }, [createButtonHoverHandlers]);

  // Clear button handlers
  const clearButtonHandlers = useMemo(() => {
    if (!hasInputs || isWorkflowRunning) {
      return {};
    }
    return createButtonHoverHandlers({
      disabled: !hasInputs || isWorkflowRunning,
      hoverStyles: {
        borderColor: 'rgb(226 232 240)',
        color: 'rgb(100 116 139)',
      },
      defaultStyles: {
        background: '#ffffff',
        borderColor: 'rgb(226 232 240)',
        color: 'rgb(100 116 139)',
      },
    });
  }, [hasInputs, isWorkflowRunning, createButtonHoverHandlers]);

  // Console button handlers
  const consoleButtonHandlers = useMemo(() => {
    if (!hasInputs) {
      return {};
    }
    return createButtonHoverHandlers({
      disabled: !hasInputs,
      hoverStyles: {
        borderColor: '#4b5563',
        color: '#4b5563',
      },
      defaultStyles: {
        background: '#ffffff',
        borderColor: '#6b7280',
        color: '#6b7280',
      },
      iconSelector: 'i',
      iconHoverColor: '#4b5563',
      iconDefaultColor: '#6b7280',
    });
  }, [hasInputs, createButtonHoverHandlers]);

  // Close button handlers
  const closeButtonHandlers = useMemo(() => {
    return createButtonHoverHandlers({
      disabled: false,
      hoverStyles: {
        background: '#f8fafc',
        borderColor: '#cbd5e1',
        color: '#475569',
      },
      defaultStyles: {
        background: '#fff',
        borderColor: '#e2e8f0',
        color: '#64748b',
      },
    });
  }, [createButtonHoverHandlers]);

  // Memoized execute button style
  const executeButtonStyle = useMemo(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: activeButton === 'run' ? '6px' : '0px',
    height: '30px',
    padding: activeButton === 'run' ? '0 12px' : '0 8px',
    backgroundColor: executeDisabled ? '#f5f5f5' : '#ffffff',
    borderRadius: '8px',
    border: executeDisabled
        ? '1px solid #d1d5db'
        : canStopPod
            ? '1px solid #dc2626'
            : '1px solid rgb(25, 135, 84)',
    fontSize: '13px',
    fontWeight: '500',
    color: executeDisabled ? '#9ca3af' : canStopPod ? '#dc2626' : 'rgb(25, 135, 84)',
    cursor: executeDisabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.3s ease',
    minWidth: '30px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    opacity: executeDisabled ? 0.6 : 1
  }), [activeButton, executeDisabled, canStopPod]);



  // Memoized publish button style (text button like Execute)
  const publishButtonStyle = useMemo(() => {
    const isDisabled = (!hasAgents && !hasTasks) || savePublishLocked || !onPublish || !projectId;
    return {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: activeButton === 'publish' ? '6px' : '0px',
      height: '30px',
      padding: activeButton === 'publish' ? '0 12px' : '0 8px',
      backgroundColor: isDisabled ? '#f5f5f5' : '#ffffff',
      borderRadius: '8px',
      border: isDisabled ? '1px solid #d1d5db' : '1px solid #38bdf8',
      fontSize: '13px',
      fontWeight: '500',
      color: isDisabled ? '#9ca3af' : '#0284c7',
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.3s ease',
      minWidth: '30px',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      opacity: isDisabled ? 0.6 : 1,
    };
  }, [activeButton, savePublishLocked, hasAgents, hasTasks, onPublish, projectId]);

  // Memoized generate button style
  const generateButtonStyle = useMemo(() => {
    const isDisabled = (!hasAgents && !hasTasks) || isWorkflowRunning || !onGenerate;
    return {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: activeButton === 'generate' ? '6px' : '0px',
      height: '30px',
      padding: activeButton === 'generate' ? '0 12px' : '0 8px',
      backgroundColor: isDisabled ? '#f5f5f5' : '#ffffff',
      borderRadius: '8px',
      border: isDisabled ? '1px solid #d1d5db' : '1px solid #a855f7',
      fontSize: '13px',
      fontWeight: '500',
      color: isDisabled ? '#9ca3af' : '#a855f7',
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.3s ease',
      minWidth: '30px',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      opacity: isDisabled ? 0.6 : 1
    };
  }, [activeButton, isWorkflowRunning, hasAgents, hasTasks, onGenerate]);

  return (
      <div style={headerStyle}>
        {/* Left side */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: '0 0 auto' }}>
          {onToggleChat && (
              <ChatToggleButton
                  isChatVisible={isChatVisible}
                  onToggle={onToggleChat}
                  iconSize={16}
              />
          )}

          {showProjectInfo && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <img
                    src={logo_icon}
                    alt="logo"
                    style={{
                      width: '32px',
                      height: '32px',
                      objectFit: 'contain'
                    }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={{
                fontSize: '1rem',
                fontWeight: 600,
                color: '#2d3748',
                lineHeight: '1.2'
              }}>
                {sanitizedProjectName}
              </span>
                  <span style={{
                    fontSize: '0.75rem',
                    color: '#6c757d',
                    lineHeight: '1.2'
                  }}>
                AI-powered project workspace
              </span>
                </div>
              </div>
          )}
        </div>

        {/* Top status pill */}
        {waitingStatus ? (
            <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 10px',
                  borderRadius: '999px',
                  background: 'linear-gradient(90deg, rgba(59,130,246,0.10), rgba(147,51,234,0.10))',
                  border: '1px solid rgba(99,102,241,0.25)',
                  color: '#334155',
                  fontSize: '12px',
                  fontWeight: 600,
                  boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
                  maxWidth: '320px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: '0 1 auto'
                }}
                title={String(waitingStatus)}
                aria-live="polite"
            >
              <i className="fa-solid fa-spinner" style={{ fontSize: '12px', color: '#6366f1' }}></i>
              <span style={{ fontWeight: 700 }}>{String(waitingStatus)}</span>
            </div>
        ) : (
            <div style={{ flex: '1 1 auto' }} />
        )}

        {/* Action bar (optimized) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'flex-end', flex: '0 0 auto' }}>
          {/* Primary actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

            <button
                onClick={() => {
                  if (canStopPod) {
                    setIsStopConfirmOpen(true);
                    return;
                  }
                  onExecute?.();
                }}
                disabled={executeDisabled}
                title={
                  isStoppingPod
                      ? "Stopping"
                      : canStopPod
                          ? "Stop"
                          : isWorkflowRunning
                              ? "Workflow is running..."
                              : (!hasAgents && !hasTasks)
                                  ? "Add an agent or task to execute"
                                  : !projectId
                                      ? "Save the project first to enable execute"
                                      : "Execute"
                }
                style={executeButtonStyle}
                {...executeButtonHandlers}
            >
              <i
                  className={`fa-solid ${isStoppingPod ? 'fa-spinner fa-spin' : canStopPod ? 'fa-circle-stop' : 'fa-play'}`}
                  style={{
                    fontSize: '12px',
                    color: executeDisabled ? '#9ca3af' : canStopPod ? '#dc2626' : 'rgb(25, 135, 84)'
                  }}
              ></i>
              <span style={{ marginLeft: '6px' }}>{executeLabel}</span>
            </button>


            {onPublish && (
                <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      borderRadius: 10,
                    }}
                >
                  <button
                      onClick={handlePublishClick}
                      disabled={(!hasAgents && !hasTasks) || savePublishLocked || !projectId}
                      title={
                        savePublishLocked
                            ? "Workflow is running..."
                            : (!hasAgents && !hasTasks)
                                ? "Add an agent or task to publish"
                                : !projectId
                                    ? "Save the project first to enable publishing"
                                    : "Publish"
                      }
                      style={{
                        ...publishButtonStyle,
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                      }}
                      {...publishButtonHandlers}
                  >
                    <i
                        className="fa-solid fa-cloud-arrow-up"
                        style={{
                          fontSize: '12px',
                          color: ((!hasAgents && !hasTasks) || savePublishLocked || !projectId) ? '#9ca3af' : '#0284c7'
                        }}
                    ></i>
                    <span style={{ marginLeft: '6px' }}>Publish</span>
                  </button>

                  <SATooltip title="View Publish History" placement="bottom" bg="#1f2937" color="#ffffff" maxWidth="260px" fontFamily="inherit">
                    <button
                        type="button"
                        onClick={() => {
                          setIsPublishedHistoryOpen(true);
                          setPublishedHistory({ content: [], totalPages: 0, totalElements: 0 });
                          setPublishedHistoryPage(0);
                          setPublishedHistoryHasMore(true);
                          fetchPublishedHistory(0, { append: false });
                        }}
                        disabled={!projectId}
                        style={{
                          ...iconBtnBaseStyle,
                          minWidth: 36,
                          width: 36,
                          padding: 0,
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                          borderColor: !projectId ? 'rgba(15,23,42,0.12)' : '#38bdf8',
                          backgroundColor: !projectId ? '#f5f5f5' : 'rgba(56,189,248,0.10)',
                          cursor: !projectId ? 'not-allowed' : 'pointer',
                          opacity: !projectId ? 0.6 : 1,
                          marginLeft: -1,
                        }}
                        aria-label="Open published history"
                        onMouseEnter={(e) => {
                          if (!projectId) return;
                          e.currentTarget.style.backgroundColor = 'rgba(56,189,248,0.16)';
                          e.currentTarget.style.borderColor = '#0ea5e9';
                        }}
                        onMouseLeave={(e) => {
                          if (!projectId) return;
                          e.currentTarget.style.backgroundColor = 'rgba(56,189,248,0.10)';
                          e.currentTarget.style.borderColor = '#38bdf8';
                        }}
                    >
                      <i className="fa-solid fa-eye" style={{ fontSize: 13, color: !projectId ? '#9ca3af' : '#0284c7' }}></i>
                    </button>
                  </SATooltip>
                </div>
            )}
          </div>

          <div style={{ width: 1, height: 22, background: 'rgba(15,23,42,0.10)' }} />

          {/* Secondary actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

            {/* Save */}
            <SATooltip title="Save workflow" placement="bottom" bg="#1f2937" color="#ffffff" maxWidth="260px" fontFamily="inherit">
              <button
                  onClick={onSave}
                  disabled={(!hasAgents && !hasTasks) || savePublishLocked}
                  style={{
                    ...iconBtnBaseStyle,
                    borderColor: '#3b82f6',
                    backgroundColor: ((!hasAgents && !hasTasks) || savePublishLocked) ? '#f5f5f5' : '#ffffff',
                    cursor: ((!hasAgents && !hasTasks) || savePublishLocked) ? 'not-allowed' : 'pointer',
                    opacity: ((!hasAgents && !hasTasks) || savePublishLocked) ? 0.6 : 1
                  }}
                  {...saveButtonHandlers}
                  aria-label="Save workflow"
              >
                <i className="fa-solid fa-floppy-disk" style={{ fontSize: '12px', color: ((!hasAgents && !hasTasks) || savePublishLocked) ? '#9ca3af' : '#3b82f6' }}></i>
              </button>
            </SATooltip>

            {/* Traces */}
            <SATooltip title="Traces" placement="bottom" bg="#1f2937" color="#ffffff" maxWidth="260px" fontFamily="inherit">
              <button
                  onClick={() => {
                    if (projectId) {
                      navigate(`/project/${projectId}/traces`);
                      return;
                    }
                    setIsHistoryDrawerOpen(true);
                  }}
                  style={{ ...iconBtnBaseStyle, borderColor: '#f49408' }}
                  {...historyButtonHandlers}
                  aria-label="Traces"
              >
                <i className="fa-solid fa-route" style={{ fontSize: '12px', color: '#f49408' }}></i>
              </button>
            </SATooltip>

            {/* Console */}
            <SATooltip title={hasInputs ? "Open console" : "Add an input to open console"} placement="bottom" bg="#1f2937" color="#ffffff" maxWidth="260px" fontFamily="inherit">
              <button
                  onClick={onConsole}
                  disabled={!hasInputs}
                  style={{
                    ...iconBtnBaseStyle,
                    borderColor: '#6b7280',
                    backgroundColor: !hasInputs ? '#f5f5f5' : '#ffffff',
                    cursor: !hasInputs ? 'not-allowed' : 'pointer',
                    opacity: !hasInputs ? 0.6 : 1
                  }}
                  {...consoleButtonHandlers}
                  aria-label="Console"
              >
                <i className="fa-solid fa-terminal" style={{ fontSize: '12px', color: !hasInputs ? '#9ca3af' : '#6b7280' }}></i>
              </button>
            </SATooltip>

            {/* More dropdown */}
            <div className="sa-workflow-header-more" style={{ position: 'relative' }}>
              <button
                  type="button"
                  onClick={() => setIsMoreOpen((v) => !v)}
                  style={iconBtnBaseStyle}
                  aria-haspopup="menu"
                  aria-expanded={isMoreOpen ? 'true' : 'false'}
                  aria-label="More actions"
              >
                <i className="fa-solid fa-ellipsis" style={{ fontSize: '12px', color: '#64748b' }}></i>
              </button>

              {isMoreOpen ? (
                  <div
                      role="menu"
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 8px)',
                        right: 0,
                        minWidth: 180,
                        padding: 8,
                        borderRadius: 12,
                        background: '#ffffff',
                        border: '1px solid rgba(15,23,42,0.10)',
                        boxShadow: '0 12px 28px rgba(15,23,42,0.14)',
                        zIndex: 1200
                      }}
                  >

                    <button
                        role="menuitem"
                        onClick={() => {
                          setIsMoreOpen(false);
                          if ((!hasAgents && !hasTasks) || isWorkflowRunning || !projectId) return;
                          handleEvaluateClick();
                        }}
                        disabled={(!hasAgents && !hasTasks) || isWorkflowRunning || !projectId}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '8px 10px',
                          borderRadius: 10,
                          border: '1px solid transparent',
                          background: 'transparent',
                          cursor: ((!hasAgents && !hasTasks) || isWorkflowRunning || !projectId) ? 'not-allowed' : 'pointer',
                          opacity: ((!hasAgents && !hasTasks) || isWorkflowRunning || !projectId) ? 0.6 : 1,
                          color: '#334155',
                          fontSize: 13,
                          fontWeight: 600
                        }}
                    >
                      <i className="fa-solid fa-clipboard-check" style={{ fontSize: 12, color: ((!hasAgents && !hasTasks) || isWorkflowRunning || !projectId) ? '#9ca3af' : '#2563eb' }}></i>
                      Evaluate
                    </button>
                    <button
                        role="menuitem"
                        onClick={() => {
                          setIsMoreOpen(false);
                          navigate(`/evaluation/${projectId}`);
                        }}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '8px 10px',
                          borderRadius: 10,
                          border: '1px solid transparent',
                          background: 'transparent',
                          cursor: 'pointer',
                          color: '#334155',
                          fontSize: 13,
                          fontWeight: 600,
                          whiteSpace: 'nowrap'
                        }}
                    >
                      <i className="fa-solid fa-arrow-up-right-from-square" style={{ fontSize: 12, color: '#0ea5e9' }}></i>
                      Evaluation dashboard
                    </button>
                    <button
                        role="menuitem"
                        onClick={() => {
                          setIsMoreOpen(false);
                          if ((!hasAgents && !hasTasks) || isWorkflowRunning || !onGenerate) return;
                          onGenerate?.();
                        }}
                        disabled={(!hasAgents && !hasTasks) || isWorkflowRunning || !onGenerate}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '8px 10px',
                          borderRadius: 10,
                          border: '1px solid transparent',
                          background: 'transparent',
                          cursor: ((!hasAgents && !hasTasks) || isWorkflowRunning || !onGenerate) ? 'not-allowed' : 'pointer',
                          opacity: ((!hasAgents && !hasTasks) || isWorkflowRunning || !onGenerate) ? 0.6 : 1,
                          color: '#334155',
                          fontSize: 13,
                          fontWeight: 600
                        }}
                    >
                      <i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize: 12, color: ((!hasAgents && !hasTasks) || isWorkflowRunning || !onGenerate) ? '#9ca3af' : '#a855f7' }}></i>
                      Generate
                    </button>

                    <button
                        role="menuitem"
                        onClick={() => {
                          setIsMoreOpen(false);
                          if (!hasInputs || isWorkflowRunning) return;
                          onClear?.();
                        }}
                        disabled={!hasInputs || isWorkflowRunning}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '8px 10px',
                          borderRadius: 10,
                          border: '1px solid transparent',
                          background: 'transparent',
                          cursor: (!hasInputs || isWorkflowRunning) ? 'not-allowed' : 'pointer',
                          opacity: (!hasInputs || isWorkflowRunning) ? 0.6 : 1,
                          color: '#334155',
                          fontSize: 13,
                          fontWeight: 600
                        }}
                    >
                      <i className="fa-solid fa-broom" style={{ fontSize: 12, color: (!hasInputs || isWorkflowRunning) ? '#9ca3af' : '#64748b' }}></i>
                      Clear
                    </button>
                  </div>
              ) : null}
            </div>

            {/* Close */}
            {!shouldHideCloseButton ? (
                <SATooltip title="Close" placement="bottom" bg="#1f2937" color="#ffffff" maxWidth="260px" fontFamily="inherit">
                  <button
                      type="button"
                      onClick={handleClose}
                      style={{ ...iconBtnBaseStyle, borderRadius: 10 }}
                      {...closeButtonHandlers}
                      aria-label="Close"
                  >
                    <i className="fa-solid fa-xmark" style={{ fontSize: '12px', color: '#64748b' }}></i>
                  </button>
                </SATooltip>
            ) : null}
          </div>
        </div>

        {/* History Drawer */}
        <WorkflowHistoryDrawer
            isOpen={isHistoryDrawerOpen}
            onClose={() => setIsHistoryDrawerOpen(false)}
            projectId={projectId}
            projectName={sanitizedProjectName}
            historyData={historyData}
        />

        {/* Published history drawer */}
        {isPublishedHistoryOpen ? (
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Published history"
                style={{
                  position: 'fixed',
                  inset: 0,
                  zIndex: 2000,
                  background: 'radial-gradient(1200px 700px at 100% 0%, rgba(56,189,248,0.18) 0%, rgba(2, 6, 23, 0.72) 45%, rgba(2, 6, 23, 0.78) 100%)',
                  display: 'flex',
                  alignItems: 'stretch',
                  justifyContent: 'flex-end',
                }}
            >
              <div
                  role="presentation"
                  onMouseDown={() => setIsPublishedHistoryOpen(false)}
                  style={{ position: 'absolute', inset: 0 }}
              />

              <div
                  onMouseDown={(e) => e.stopPropagation()}
                  style={{
                    position: 'relative',
                    height: '100%',
                    width: '100%',
                    maxWidth: 480,
                    marginLeft: 'auto',
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)',
                    borderLeft: '1px solid rgba(15,23,42,0.10)',
                    boxShadow: '-22px 0 70px rgba(2, 6, 23, 0.34)',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
              >
                <div
                    style={{
                      padding: '16px 16px',
                      borderBottom: '1px solid rgba(15,23,42,0.08)',
                      background: 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.72) 100%)',
                      backdropFilter: 'blur(12px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 10,
                    }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div
                          style={{
                            width: 34,
                            height: 34,
                            borderRadius: 12,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'linear-gradient(135deg, rgba(79,70,229,0.14) 0%, rgba(14,165,233,0.12) 100%)',
                            border: '1px solid rgba(99,102,241,0.16)',
                            flexShrink: 0,
                          }}
                      >
                        <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 14, color: '#4f46e5' }} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 950, color: '#0f172a', lineHeight: 1.15, letterSpacing: '-0.01em' }}>
                          Published history
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 3, lineHeight: 1.25 }}>
                          Versions published for this workflow.
                        </div>
                      </div>
                    </div>
                  </div>

                  <button
                      type="button"
                      onClick={() => setIsPublishedHistoryOpen(false)}
                      aria-label="Close published history drawer"
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 10,
                        border: '1px solid rgba(15,23,42,0.10)',
                        background: 'rgba(255,255,255,0.94)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#475569',
                        flexShrink: 0,
                        transition: 'transform 120ms ease, background 120ms ease, border-color 120ms ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#ffffff';
                        e.currentTarget.style.borderColor = 'rgba(15,23,42,0.16)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.94)';
                        e.currentTarget.style.borderColor = 'rgba(15,23,42,0.10)';
                        e.currentTarget.style.transform = 'translateY(0px)';
                      }}
                  >
                    <i className="fa-solid fa-xmark" style={{ fontSize: 14, color: '#64748b' }} />
                  </button>
                </div>

                <div
                    ref={publishedHistoryScrollRef}
                    className="sa-published-history-scroll"
                    style={{
                      padding: 16,
                      overflowY: 'auto',
                      flex: '1 1 auto',
                      scrollBehavior: 'smooth',
                      overscrollBehavior: 'contain',
                    }}
                    onScroll={(e) => {
                      if (publishedHistoryLoading) return;
                      if (!publishedHistoryHasMore) return;
                      const el = e.currentTarget;
                      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
                      if (remaining < 220) {
                        fetchPublishedHistory(publishedHistoryPage + 1, { append: true });
                      }
                    }}
                >
                  {publishedHistoryError ? (
                      <div
                          style={{
                            padding: '10px 12px',
                            borderRadius: 12,
                            background: 'rgba(239,68,68,0.08)',
                            border: '1px solid rgba(239,68,68,0.20)',
                            fontSize: 12,
                            fontWeight: 800,
                            color: '#b91c1c',
                          }}
                      >
                        {publishedHistoryError}
                      </div>
                  ) : null}

                  {publishedHistoryLoading ? (
                      <div style={{ fontSize: 13, color: '#64748b', padding: '8px 2px' }}>
                        Loading…
                      </div>
                  ) : (
                      <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 10,
                            background: 'transparent',
                          }}
                      >
                        {(publishedHistory?.content || []).length === 0 ? (
                            <div
                                style={{
                                  fontSize: 13,
                                  color: '#64748b',
                                  padding: 14,
                                  borderRadius: 16,
                                  border: '1px dashed rgba(15,23,42,0.14)',
                                  background: 'rgba(255,255,255,0.75)',
                                }}
                            >
                              No published versions yet.
                            </div>
                        ) : (
                            (publishedHistory.content || []).map((it, idx) => {
                              const title = sanitizeText(it?.name || 'Untitled');
                              const id = it?.id ? String(it.id) : '';
                              const createdIso = it?.createdDate ? String(it.createdDate) : '';
                              const createdLabel = createdIso ? formatTimestamp(createdIso) : '';
                              const author = sanitizeText(it?.author || it?.createdBy || it?.userName || '') || '';
                              const statusRaw = sanitizeText(it?.status || '') || '';
                              const description =
                                  sanitizeText(
                                      it?.description ||
                                      it?.desc ||
                                      it?.publishDescription ||
                                      it?.comment ||
                                      it?.comments ||
                                      ''
                                  ) || '';
                              const appConfigCandidate = it?.appConfig ?? it?.app_config ?? it?.config ?? null;
                              let appConfig = null;
                              if (appConfigCandidate && typeof appConfigCandidate === 'object') {
                                appConfig = appConfigCandidate;
                              } else if (typeof appConfigCandidate === 'string') {
                                const s = appConfigCandidate.trim();
                                if (s) {
                                  try {
                                    appConfig = JSON.parse(s);
                                  } catch {
                                    appConfig = { _raw: s };
                                  }
                                }
                              }
                              const appName = sanitizeText(appConfig?.appName || appConfig?.name || '') || '';
                              const llms = Array.isArray(appConfig?.llm) ? appConfig.llm : [];
                              const handlers = Array.isArray(appConfig?.handlerConfig) ? appConfig.handlerConfig : [];
                              const agentsRaw = Array.isArray(appConfig?.agentConfig) ? appConfig.agentConfig : [];
                              const agents = (() => {
                                const out = [];
                                const seen = new Set();
                                for (const a of agentsRaw) {
                                  const idKey = a?.agentId != null ? String(a.agentId) : '';
                                  const nameKey = a?.name != null ? String(a.name) : '';
                                  const titleKey = a?.title != null ? String(a.title) : '';
                                  const key = (idKey || nameKey || titleKey || JSON.stringify(a || {})).trim();
                                  if (!key) continue;
                                  if (seen.has(key)) continue;
                                  seen.add(key);
                                  out.push(a);
                                }
                                return out;
                              })();
                              const agentDisplayTitles = (() => {
                                const bases = agents.map((a, j) => {
                                  const raw = sanitizeText(a?.title || a?.name || '') || '';
                                  return raw || `Agent ${j + 1}`;
                                });
                                const counts = {};
                                bases.forEach((b) => {
                                  counts[b] = (counts[b] || 0) + 1;
                                });
                                const ordinal = {};
                                return agents.map((a, j) => {
                                  const base = bases[j];
                                  if (counts[base] <= 1) return base;
                                  const idRaw = a?.agentId != null ? String(a.agentId) : a?.id != null ? String(a.id) : '';
                                  const id = sanitizeText(idRaw) || '';
                                  ordinal[base] = (ordinal[base] || 0) + 1;
                                  const n = ordinal[base];
                                  if (id) {
                                    const short = id.length > 12 ? `${id.slice(0, 8)}…` : id;
                                    return `${base} (${short})`;
                                  }
                                  return `${base} (#${n})`;
                                });
                              })();
                              const isLive = Boolean(
                                  it?.isLive === true ||
                                  statusRaw.toLowerCase?.() === 'active'
                              );
                              const isCurlOpen = Boolean(id && expandedPublishedCurlId === id);
                              const publishTab = publishedHistoryDetailTab[id] || 'details';
                              const isCurlHovered = Boolean(id && hoveredPublishedCurlId === id);
                              const curl = id ? buildPublishedHistoryCurl(id) : '';
                              const isRestoringThis = Boolean(id) && restoringPublishedId === id;
                              const canRestoreAsDefault = Boolean(id) && !isLive && !isRestoringThis;

                              return (
                                  <div
                                      key={id || title}
                                      style={{
                                        padding: 12,
                                        position: 'relative',
                                        borderRadius: 16,
                                        background: isCurlOpen ? 'rgba(79,70,229,0.06)' : 'rgba(255,255,255,0.86)',
                                        border: isCurlOpen
                                            ? '1px solid rgba(79,70,229,0.18)'
                                            : '1px solid rgba(15,23,42,0.10)',
                                        boxShadow: isCurlOpen ? '0 10px 24px rgba(2,6,23,0.08)' : '0 1px 0 rgba(2,6,23,0.04)',
                                        transition: 'background 140ms ease, border-color 140ms ease, box-shadow 140ms ease, transform 140ms ease',
                                      }}
                                      onMouseEnter={(e) => {
                                        if (id) setHoveredPublishedHistoryId(id);
                                        if (isCurlOpen) return;
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                        e.currentTarget.style.boxShadow = '0 10px 24px rgba(2,6,23,0.10)';
                                        e.currentTarget.style.borderColor = 'rgba(15,23,42,0.14)';
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!isCurlOpen) {
                                          e.currentTarget.style.transform = 'translateY(0px)';
                                          e.currentTarget.style.boxShadow = '0 1px 0 rgba(2,6,23,0.04)';
                                          e.currentTarget.style.borderColor = 'rgba(15,23,42,0.10)';
                                        }
                                        setHoveredPublishedHistoryId((prev) => (prev === id ? null : prev));
                                      }}
                                  >
                                    {!isLive && hoveredPublishedHistoryId === id ? (
                                        <button
                                            type="button"
                                            disabled={!canRestoreAsDefault}
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              if (!id) return;
                                              setRestoreConfirmData({ id, title, description });
                                              setIsRestoreConfirmOpen(true);
                                            }}
                                            style={{
                                              position: 'absolute',
                                              top: 10,
                                              right: 10,
                                              height: 26,
                                              padding: '0 8px',
                                              borderRadius: 999,
                                              border: canRestoreAsDefault
                                                  ? '1px solid rgba(79,70,229,0.28)'
                                                  : '1px solid rgba(148,163,184,0.28)',
                                              background: canRestoreAsDefault
                                                  ? 'linear-gradient(135deg, rgba(79,70,229,0.10) 0%, rgba(14,165,233,0.08) 100%)'
                                                  : 'rgba(241,245,249,0.8)',
                                              color: canRestoreAsDefault ? '#3730a3' : '#94a3b8',
                                              cursor: canRestoreAsDefault ? 'pointer' : 'not-allowed',
                                              fontSize: 10,
                                              fontWeight: 950,
                                              letterSpacing: '0.01em',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: 6,
                                              transition: 'all 140ms ease',
                                              zIndex: 12,
                                            }}
                                            title={
                                              isRestoringThis
                                                  ? 'Restoring...'
                                                  : 'Restore this version as default template'
                                            }
                                            aria-label="Restore as default template"
                                        >
                                          <i className="fa-solid fa-rotate-left" style={{ fontSize: 10 }} />
                                          {isRestoringThis ? 'Restoring...' : 'Restore'}
                                        </button>
                                    ) : null}

                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                                      {/* left action (expand) */}
                                      <div style={{ flexShrink: 0, paddingTop: 1 }}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                              if (!id) return;
                                              setExpandedPublishedCurlId((prev) => {
                                                const next = prev === id ? null : id;
                                                if (next) {
                                                  setPublishedHistoryDetailTab((t) => ({
                                                    ...t,
                                                    [id]: t[id] || 'details',
                                                  }));
                                                }
                                                return next;
                                              });
                                            }}
                                            disabled={!id}
                                            style={{
                                              width: 32,
                                              height: 32,
                                              borderRadius: 12,
                                              border: '1px solid rgba(15,23,42,0.10)',
                                              background: isCurlOpen ? '#ffffff' : '#f8fafc',
                                              color: '#64748b',
                                              cursor: id ? 'pointer' : 'not-allowed',
                                              opacity: id ? 1 : 0.6,
                                              boxShadow: '0 1px 0 rgba(2,6,23,0.03)',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              transition: 'background 140ms ease, border-color 140ms ease, transform 140ms ease',
                                              padding: 0,
                                            }}
                                            title={isCurlOpen ? 'Hide API & app config' : 'Show API & app config'}
                                            aria-label={isCurlOpen ? 'Hide API & app config' : 'Show API & app config'}
                                            onMouseEnter={(e) => {
                                              if (!id) return;
                                              e.currentTarget.style.background = '#ffffff';
                                              e.currentTarget.style.borderColor = 'rgba(15,23,42,0.16)';
                                              e.currentTarget.style.transform = 'translateY(-1px)';
                                            }}
                                            onMouseLeave={(e) => {
                                              if (!id) return;
                                              e.currentTarget.style.background = isCurlOpen ? '#ffffff' : '#f8fafc';
                                              e.currentTarget.style.borderColor = 'rgba(15,23,42,0.10)';
                                              e.currentTarget.style.transform = 'translateY(0px)';
                                            }}
                                        >
                                          <i
                                              className={`fa-solid ${isCurlOpen ? 'fa-chevron-down' : 'fa-chevron-right'}`}
                                              style={{ fontSize: 12, opacity: 0.85 }}
                                          />
                                        </button>
                                      </div>

                                      <div style={{ minWidth: 0, flex: 1 }}>

                                        {/* display name */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                                          <div
                                              style={{
                                                fontSize: 13,
                                                fontWeight: 950,
                                                color: '#0f172a',
                                                lineHeight: 1.2,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                letterSpacing: '-0.01em',
                                                minWidth: 0,
                                              }}
                                              title={title}
                                          >
                                            {title}
                                          </div>
                                        </div>

                                        {/* meta row (created by + timestamp) */}
                                        <div
                                            style={{
                                              marginTop: 8,
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 8,
                                              flexWrap: 'wrap',
                                            }}
                                        >
                                          {author ? (
                                              <span
                                                  style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 6,
                                                    height: 22,
                                                    borderRadius: 999,
                                                    color: '#475569',
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    maxWidth: '100%',
                                                  }}
                                                  title={author}
                                              >
                                    <i className="fa-solid fa-user" style={{ fontSize: 10, opacity: 0.8 }} />
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {author}
                                    </span>
                                  </span>
                                          ) : null}

                                          {createdLabel ? (
                                              <span
                                                  style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 6,
                                                    height: 22,
                                                    padding: '0 10px',
                                                    fontSize: 11,
                                                    letterSpacing: '-0.01em',
                                                  }}
                                                  title={createdLabel}
                                              >
                                    <i className="fa-solid fa-clock" style={{ fontSize: 10, opacity: 0.85 }} />
                                                {createdLabel}
                                  </span>
                                          ) : null}
                                        </div>

                                        {description ? (
                                            <div
                                                style={{
                                                  marginTop: 6,
                                                  color: '#64748b',
                                                  fontSize: 12,
                                                  lineHeight: 1.3,
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                                  display: '-webkit-box',
                                                  WebkitLineClamp: 2,
                                                  WebkitBoxOrient: 'vertical',
                                                }}
                                                title={description}
                                            >
                                              {description}
                                            </div>
                                        ) : null}

                                        <div style={{ marginTop: description ? 6 : 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                          {isLive ? (
                                              <span
                                                  style={{
                                                    height: 28,
                                                    padding: '0 10px',
                                                    borderRadius: 999,
                                                    border: '1px solid rgba(34,197,94,0.22)',
                                                    background: 'rgba(34,197,94,0.08)',
                                                    color: '#15803d',
                                                    fontSize: 11,
                                                    fontWeight: 900,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 6,
                                                  }}
                                              >
                                    <i className="fa-solid fa-circle-check" style={{ fontSize: 11 }} />
                                    Default template
                                  </span>
                                          ) : null}
                                        </div>
                                      </div>
                                    </div>

                                    {isCurlOpen ? (
                                        <div
                                            style={{
                                              marginTop: 12,
                                              borderRadius: 14,
                                              border: '1px solid rgba(15,23,42,0.10)',
                                              background:
                                                  'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.96) 100%)',
                                              padding: 12,
                                            }}
                                        >
                                          <div
                                              role="tablist"
                                              aria-label="Published version sections"
                                              style={{
                                                display: 'flex',
                                                gap: 18,
                                                marginBottom: 12,
                                                padding: 0,
                                                borderRadius: 0,
                                                border: 'none',
                                                background: 'transparent',
                                                flexWrap: 'wrap',
                                                borderBottom: '1px solid rgba(15,23,42,0.10)',
                                              }}
                                          >
                                            <button
                                                type="button"
                                                role="tab"
                                                aria-selected={publishTab === 'details'}
                                                onClick={() =>
                                                    setPublishedHistoryDetailTab((p) => ({ ...p, [id]: 'details' }))
                                                }
                                                style={{
                                                  height: 40,
                                                  padding: '0 2px',
                                                  borderRadius: 0,
                                                  border: 'none',
                                                  background: 'transparent',
                                                  color: publishTab === 'details' ? '#0f172a' : '#64748b',
                                                  fontSize: 12,
                                                  fontWeight: 900,
                                                  cursor: 'pointer',
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  gap: 8,
                                                  boxShadow: 'none',
                                                  borderBottom: publishTab === 'details' ? '2px solid #0ea5e9' : '2px solid transparent',
                                                  marginBottom: -1,
                                                }}
                                            >
                                              <i
                                                  className="fa-solid fa-file-lines"
                                                  style={{ fontSize: 11, color: publishTab === 'details' ? '#0284c7' : '#94a3b8' }}
                                              />
                                              Publish details
                                            </button>
                                            <button
                                                type="button"
                                                role="tab"
                                                aria-selected={publishTab === 'appInfo'}
                                                onClick={() =>
                                                    setPublishedHistoryDetailTab((p) => ({ ...p, [id]: 'appInfo' }))
                                                }
                                                style={{
                                                  height: 40,
                                                  padding: '0 2px',
                                                  borderRadius: 0,
                                                  border: 'none',
                                                  background: 'transparent',
                                                  color: publishTab === 'appInfo' ? '#0f172a' : '#64748b',
                                                  fontSize: 12,
                                                  fontWeight: 900,
                                                  cursor: 'pointer',
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  gap: 8,
                                                  boxShadow: 'none',
                                                  borderBottom: publishTab === 'appInfo' ? '2px solid #22c55e' : '2px solid transparent',
                                                  marginBottom: -1,
                                                }}
                                            >
                                              <i
                                                  className="fa-solid fa-diagram-project"
                                                  style={{ fontSize: 11, color: publishTab === 'appInfo' ? '#16a34a' : '#94a3b8' }}
                                              />
                                              App info
                                            </button>
                                          </div>

                                          {publishTab === 'details' ? (
                                              <>
                                                <div
                                                    style={{
                                                      marginBottom: 12,
                                                      borderRadius: 12,
                                                      border: '1px solid rgba(15,23,42,0.08)',
                                                      background: 'rgba(255,255,255,0.9)',
                                                      padding: 12,
                                                      display: 'grid',
                                                      gap: 10,
                                                    }}
                                                >
                                                  <div
                                                      style={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        alignItems: 'center',
                                                        gap: 10,
                                                        justifyContent: 'space-between',
                                                      }}
                                                  >
                                                    <div style={{ fontSize: 11, fontWeight: 900, color: '#64748b' }}>
                                                      Publish name
                                                    </div>
                                                    <div
                                                        style={{
                                                          fontSize: 12,
                                                          fontWeight: 900,
                                                          color: '#0f172a',
                                                          textAlign: 'right',
                                                          maxWidth: '70%',
                                                        }}
                                                        title={title}
                                                    >
                                                      {title}
                                                    </div>
                                                  </div>
                                                  <div
                                                      style={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        alignItems: 'center',
                                                        gap: 10,
                                                        justifyContent: 'space-between',
                                                      }}
                                                  >
                                                    <div style={{ fontSize: 11, fontWeight: 900, color: '#64748b' }}>
                                                      Publish Id
                                                    </div>
                                                    <div
                                                        style={{
                                                          display: 'inline-flex',
                                                          alignItems: 'center',
                                                          gap: 8,
                                                          minWidth: 0,
                                                          maxWidth: '100%',
                                                        }}
                                                    >
                                        <span
                                            style={{
                                              fontSize: 11,
                                              fontWeight: 900,
                                              color: '#0f172a',
                                              fontFamily:
                                                  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                            }}
                                            title={id}
                                        >
                                          {id}
                                        </span>
                                                      <button
                                                          type="button"
                                                          onClick={async () => {
                                                            if (!id) return;
                                                            const ok = await copyTextToClipboard(id);
                                                            if (ok) showNotificationGlobal('ID copied', 'success', 'bottomRight');
                                                            else showNotificationGlobal('Failed to copy', 'error', 'bottomRight');
                                                          }}
                                                          disabled={!id}
                                                          style={{
                                                            width: 28,
                                                            height: 28,
                                                            borderRadius: 12,
                                                            border: '1px solid rgba(15,23,42,0.10)',
                                                            background: '#f8fafc',
                                                            cursor: id ? 'pointer' : 'not-allowed',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: '#64748b',
                                                            padding: 0,
                                                          }}
                                                          title="Copy ID"
                                                          aria-label="Copy ID"
                                                      >
                                                        <i className="fa-solid fa-copy" style={{ fontSize: 11 }} />
                                                      </button>
                                                    </div>
                                                  </div>
                                                  {statusRaw || isLive ? (
                                                      <div
                                                          style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            gap: 10,
                                                            flexWrap: 'wrap',
                                                          }}
                                                      >
                                                        <div style={{ fontSize: 11, fontWeight: 900, color: '#64748b' }}>
                                                          Status
                                                        </div>
                                                        <div style={{ fontSize: 11, fontWeight: 900, color: '#0f172a' }}>
                                                          {isLive ? 'Active' : statusRaw || '—'}
                                                        </div>
                                                      </div>
                                                  ) : null}
                                                  {author ? (
                                                      <div
                                                          style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            gap: 10,
                                                            flexWrap: 'wrap',
                                                          }}
                                                      >
                                                        <div style={{ fontSize: 11, fontWeight: 900, color: '#64748b' }}>
                                                          Created by
                                                        </div>
                                                        <div
                                                            style={{ fontSize: 11, fontWeight: 900, color: '#0f172a' }}
                                                            title={author}
                                                        >
                                                          {author}
                                                        </div>
                                                      </div>
                                                  ) : null}
                                                  {createdLabel ? (
                                                      <div
                                                          style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            gap: 10,
                                                            flexWrap: 'wrap',
                                                          }}
                                                      >
                                                        <div style={{ fontSize: 11, fontWeight: 900, color: '#64748b' }}>
                                                          Created at
                                                        </div>
                                                        <div
                                                            style={{ fontSize: 11, fontWeight: 900, color: '#0f172a' }}
                                                            title={createdLabel}
                                                        >
                                                          {createdLabel}
                                                        </div>
                                                      </div>
                                                  ) : null}
                                                  {description ? (
                                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                        <div style={{ fontSize: 11, fontWeight: 900, color: '#64748b' }}>
                                                          Description
                                                        </div>
                                                        <div
                                                            style={{
                                                              fontSize: 12,
                                                              fontWeight: 800,
                                                              color: '#475569',
                                                              lineHeight: 1.45,
                                                              whiteSpace: 'pre-wrap',
                                                              wordBreak: 'break-word',
                                                            }}
                                                        >
                                                          {description}
                                                        </div>
                                                      </div>
                                                  ) : null}
                                                </div>

                                                <div
                                                    style={{
                                                      borderRadius: 14,
                                                      border: '1px solid rgba(15,23,42,0.10)',
                                                      background: '#f1f5f9',
                                                      padding: 10,
                                                    }}
                                                >
                                                  <div
                                                      style={{
                                                        fontSize: 11,
                                                        fontWeight: 950,
                                                        color: '#64748b',
                                                        marginBottom: 8,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 8,
                                                      }}
                                                  >
                                                    <i className="fa-solid fa-terminal" style={{ fontSize: 11, color: '#475569' }} />
                                                    cURL
                                                  </div>
                                                  <div
                                                      style={{ position: 'relative' }}
                                                      onMouseEnter={() => {
                                                        if (!id) return;
                                                        setHoveredPublishedCurlId(id);
                                                      }}
                                                      onMouseLeave={() => {
                                                        if (hoveredPublishedCurlId === id) setHoveredPublishedCurlId(null);
                                                      }}
                                                  >
                                                    <button
                                                        type="button"
                                                        onClick={async () => {
                                                          if (!id) return;
                                                          const ok = await copyTextToClipboard(curl);
                                                          if (ok) showNotificationGlobal('cURL copied', 'success', 'bottomRight');
                                                          else showNotificationGlobal('Failed to copy', 'error', 'bottomRight');
                                                        }}
                                                        disabled={!id}
                                                        style={{
                                                          position: 'absolute',
                                                          top: 10,
                                                          right: 10,
                                                          zIndex: 2,
                                                          height: 30,
                                                          width: 34,
                                                          padding: 0,
                                                          borderRadius: 12,
                                                          border: '1px solid rgba(255,255,255,0.12)',
                                                          background: 'rgba(255,255,255,0.06)',
                                                          color: '#e2e8f0',
                                                          fontSize: 12,
                                                          fontWeight: 900,
                                                          cursor: id ? 'pointer' : 'not-allowed',
                                                          opacity: id ? (isCurlHovered ? 1 : 0) : 0,
                                                          pointerEvents: id ? (isCurlHovered ? 'auto' : 'none') : 'none',
                                                          display: 'inline-flex',
                                                          alignItems: 'center',
                                                          justifyContent: 'center',
                                                          transition: 'transform 120ms ease, background 120ms ease',
                                                        }}
                                                        title="Copy cURL"
                                                        aria-label="Copy cURL"
                                                        onFocus={() => {
                                                          if (!id) return;
                                                          setHoveredPublishedCurlId(id);
                                                        }}
                                                        onBlur={() => {
                                                          if (hoveredPublishedCurlId === id) setHoveredPublishedCurlId(null);
                                                        }}
                                                        onMouseEnter={(e) => {
                                                          if (!id) return;
                                                          e.currentTarget.style.background = 'rgba(255,255,255,0.10)';
                                                          e.currentTarget.style.transform = 'translateY(-1px)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                          if (!id) return;
                                                          e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                                                          e.currentTarget.style.transform = 'translateY(0px)';
                                                        }}
                                                    >
                                                      <i className="fa-solid fa-copy" style={{ fontSize: 12, color: '#e2e8f0' }} />
                                                    </button>

                                                    <pre
                                                        style={{
                                                          margin: 0,
                                                          padding: 12,
                                                          paddingRight: 60,
                                                          borderRadius: 14,
                                                          background: '#0b1220',
                                                          border: '1px solid rgba(2,6,23,0.20)',
                                                          fontSize: 11,
                                                          color: '#e2e8f0',
                                                          overflowX: 'auto',
                                                          whiteSpace: 'pre',
                                                          fontFamily:
                                                              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                                        }}
                                                    >
                                    {renderCurlHighlighted(curl)}
                                  </pre>
                                                  </div>
                                                </div>
                                              </>
                                          ) : null}

                                          {publishTab === 'appInfo' ? (
                                              appConfig ? (
                                                  <div
                                                      style={{
                                                        marginTop: 0,
                                                        borderRadius: 14,
                                                        border: 'none',
                                                        background: 'transparent',
                                                        padding: 0,
                                                      }}
                                                  >
                                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                                                      <div style={{ minWidth: 0 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                                          <div
                                                              style={{
                                                                fontSize: 12,
                                                                fontWeight: 950,
                                                                color: '#0f172a',
                                                                letterSpacing: '-0.01em',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: 8,
                                                              }}
                                                          >
                                          <span
                                              style={{
                                                width: 26,
                                                height: 26,
                                                borderRadius: 10,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                background: 'rgba(2,132,199,0.10)',
                                                border: '1px solid rgba(2,132,199,0.16)',
                                                flexShrink: 0,
                                              }}
                                          >
                                            <i className="fa-solid fa-diagram-project" style={{ fontSize: 12, color: '#0284c7' }} />
                                          </span>
                                                            {appName ? (
                                                                <span
                                                                    style={{
                                                                      overflow: 'hidden',
                                                                      textOverflow: 'ellipsis',
                                                                      whiteSpace: 'nowrap',
                                                                      maxWidth: 280,
                                                                    }}
                                                                    title={appName}
                                                                >
                                              {appName}
                                            </span>
                                                            ) : (
                                                                <span style={{ color: '#334155' }}>App config</span>
                                                            )}
                                                          </div>
                                                          <span
                                                              style={{
                                                                height: 22,
                                                                padding: '0 10px',
                                                                borderRadius: 999,
                                                                border: 'none',
                                                                background: 'rgba(2,132,199,0.08)',
                                                                fontSize: 11,
                                                                fontWeight: 800,
                                                                color: '#075985',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: 6,
                                                              }}
                                                              title="LLMs configured"
                                                          >
                                          <i className="fa-solid fa-microchip" style={{ fontSize: 10, opacity: 0.85 }} />
                                                            {llms.length} LLM{llms.length === 1 ? '' : 's'}
                                        </span>
                                                          <span
                                                              style={{
                                                                height: 22,
                                                                padding: '0 10px',
                                                                borderRadius: 999,
                                                                border: 'none',
                                                                background: 'rgba(2,132,199,0.08)',
                                                                fontSize: 11,
                                                                fontWeight: 800,
                                                                color: '#075985',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: 6,
                                                              }}
                                                              title="Handlers configured"
                                                          >
                                          <i className="fa-solid fa-plug" style={{ fontSize: 10, opacity: 0.85 }} />
                                                            {handlers.length} tool{handlers.length === 1 ? '' : 's'}
                                        </span>
                                                          <span
                                                              style={{
                                                                height: 22,
                                                                padding: '0 10px',
                                                                borderRadius: 999,
                                                                border: 'none',
                                                                background: 'rgba(2,132,199,0.08)',
                                                                fontSize: 11,
                                                                fontWeight: 800,
                                                                color: '#075985',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: 6,
                                                              }}
                                                              title="Agents configured"
                                                          >
                                          <i className="fa-solid fa-user-gear" style={{ fontSize: 10, opacity: 0.85 }} />
                                                            {agents.length} agent{agents.length === 1 ? '' : 's'}
                                        </span>
                                                        </div>
                                                      </div>

                                                      <button
                                                          type="button"
                                                          onClick={async () => {
                                                            const raw =
                                                                typeof appConfigCandidate === 'string'
                                                                    ? appConfigCandidate
                                                                    : JSON.stringify(appConfigCandidate ?? appConfig ?? {}, null, 2);
                                                            const ok = await copyTextToClipboard(raw);
                                                            if (ok) showNotificationGlobal('App config copied', 'success', 'bottomRight');
                                                            else showNotificationGlobal('Failed to copy', 'error', 'bottomRight');
                                                          }}
                                                          style={{
                                                            flexShrink: 0,
                                                            height: 30,
                                                            padding: '0 10px',
                                                            borderRadius: 12,
                                                            border: '1px solid rgba(15,23,42,0.10)',
                                                            background: '#ffffff',
                                                            cursor: 'pointer',
                                                            color: '#334155',
                                                            fontSize: 11,
                                                            fontWeight: 900,
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: 8,
                                                            boxShadow: '0 1px 0 rgba(2,6,23,0.03)',
                                                          }}
                                                          title="Copy app config JSON"
                                                          aria-label="Copy app config JSON"
                                                      >
                                                        <i className="fa-solid fa-copy" style={{ fontSize: 11, color: '#0284c7' }} />
                                                        Copy App Config
                                                      </button>
                                                    </div>

                                                    <div
                                                        style={{
                                                          marginTop: 12,
                                                          display: 'grid',
                                                          gridTemplateColumns: '1fr',
                                                          gap: 12,
                                                        }}
                                                    >
                                                      <div
                                                          style={{
                                                            borderRadius: 14,
                                                            border: 'none',
                                                            background: 'rgba(2,6,23,0.03)',
                                                            padding: 12,
                                                          }}
                                                      >
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                                                          <div style={{ fontSize: 11, fontWeight: 950, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            <i className="fa-solid fa-microchip" style={{ color: '#4f46e5' }} />
                                                            LLMs
                                                          </div>
                                                          <div style={{ fontSize: 11, fontWeight: 900, color: '#64748b' }}>{llms.length}</div>
                                                        </div>
                                                        {llms.length ? (
                                                            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                              {llms.slice(0, 5).map((l, j) => {
                                                                const t = sanitizeText(l?.title || l?.name || `LLM ${j + 1}`) || `LLM ${j + 1}`;
                                                                const model = sanitizeText(l?.llmConfig?.model || l?.model || '') || '';
                                                                const type = sanitizeText(l?.llmConfig?.llmType || l?.type || '') || '';
                                                                const subtitle = [type, model].filter(Boolean).join(' • ');
                                                                return (
                                                                    <div key={`${t}-${j}`} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                      <div style={{ fontSize: 12, fontWeight: 900, color: '#0f172a', lineHeight: 1.2 }}>{t}</div>
                                                                      {subtitle ? (
                                                                          <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', lineHeight: 1.2 }}>
                                                                            {subtitle}
                                                                          </div>
                                                                      ) : null}
                                                                    </div>
                                                                );
                                                              })}
                                                              {llms.length > 5 ? (
                                                                  <div style={{ fontSize: 11, fontWeight: 900, color: '#64748b' }}>+{llms.length - 5} more</div>
                                                              ) : null}
                                                            </div>
                                                        ) : (
                                                            <div style={{ marginTop: 8, fontSize: 11, fontWeight: 800, color: '#94a3b8' }}>No LLMs configured.</div>
                                                        )}
                                                      </div>

                                                      <div
                                                          style={{
                                                            borderRadius: 14,
                                                            border: 'none',
                                                            background: 'rgba(2,6,23,0.03)',
                                                            padding: 12,
                                                          }}
                                                      >
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                                                          <div style={{ fontSize: 11, fontWeight: 950, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            <i className="fa-solid fa-plug" style={{ color: '#0284c7' }} />
                                                            Tools
                                                          </div>
                                                          <div style={{ fontSize: 11, fontWeight: 900, color: '#64748b' }}>{handlers.length}</div>
                                                        </div>
                                                        {handlers.length ? (
                                                            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                              {handlers.slice(0, 5).map((h, j) => {
                                                                const t = sanitizeText(h?.title || h?.name || `Handler ${j + 1}`) || `Handler ${j + 1}`;
                                                                const handlerName = sanitizeText(h?.handlerName || '') || '';
                                                                const subtitle = [handlerName].filter(Boolean).join(' • ');
                                                                const attrArr = Array.isArray(h?.attribute) ? h.attribute : [];
                                                                const primaryAttr = attrArr?.[0] || null;
                                                                const hasOauth = Boolean(primaryAttr?.hasOauth === true);
                                                                const provider = sanitizeText(primaryAttr?.provider || '') || '';
                                                                const llmConfigRef = sanitizeText(primaryAttr?.llm_config || primaryAttr?.llmConfig || '') || '';
                                                                const providerIcon = provider ? getProviderIconEl(provider) : null;
                                                                return (
                                                                    <div key={`${t}-${j}`} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                                                        <div
                                                                            style={{
                                                                              fontSize: 12,
                                                                              fontWeight: 900,
                                                                              color: '#0f172a',
                                                                              lineHeight: 1.2,
                                                                              overflow: 'hidden',
                                                                              textOverflow: 'ellipsis',
                                                                              whiteSpace: 'nowrap',
                                                                            }}
                                                                            title={t}
                                                                        >
                                                                          {t}
                                                                        </div>
                                                                      </div>
                                                                      {subtitle ? (
                                                                          <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', lineHeight: 1.2 }}>
                                                                            {subtitle}
                                                                          </div>
                                                                      ) : null}
                                                                      {(hasOauth || provider || llmConfigRef) ? (
                                                                          <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                                            {llmConfigRef ? (
                                                                                <span
                                                                                    style={{
                                                                                      height: 20,
                                                                                      padding: '0 8px',
                                                                                      borderRadius: 999,
                                                                                      background: 'rgba(99,102,241,0.08)',
                                                                                      border: '1px solid rgba(99,102,241,0.16)',
                                                                                      color: '#3730a3',
                                                                                      fontSize: 10,
                                                                                      fontWeight: 900,
                                                                                    }}
                                                                                    title={`llm_config: ${llmConfigRef}`}
                                                                                >
                                                        llm_config: {llmConfigRef}
                                                      </span>
                                                                            ) : null}
                                                                            {hasOauth ? (
                                                                                <span
                                                                                    style={{
                                                                                      height: 20,
                                                                                      padding: '0 8px',
                                                                                      borderRadius: 999,
                                                                                      background: 'rgba(14,165,233,0.10)',
                                                                                      border: '1px solid rgba(14,165,233,0.18)',
                                                                                      color: '#075985',
                                                                                      fontSize: 10,
                                                                                      fontWeight: 900,
                                                                                      display: 'inline-flex',
                                                                                      alignItems: 'center',
                                                                                      gap: 6,
                                                                                    }}
                                                                                    title="OAuth enabled"
                                                                                >
                                                        <i className="fa-solid fa-shield" style={{ fontSize: 10 }} />
                                                        OAuth
                                                      </span>
                                                                            ) : null}
                                                                            {provider ? (
                                                                                <span
                                                                                    style={{
                                                                                      height: 20,
                                                                                      padding: '0 8px',
                                                                                      borderRadius: 999,
                                                                                      background: 'rgba(15,23,42,0.04)',
                                                                                      border: '1px solid rgba(15,23,42,0.10)',
                                                                                      color: '#475569',
                                                                                      fontSize: 10,
                                                                                      fontWeight: 900,
                                                                                      display: 'inline-flex',
                                                                                      alignItems: 'center',
                                                                                      gap: 6,
                                                                                    }}
                                                                                    title={`provider: ${provider}`}
                                                                                >
                                                        provider: {getProviderIconEl(provider)} {provider}
                                                      </span>
                                                                            ) : null}
                                                                          </div>
                                                                      ) : null}
                                                                    </div>
                                                                );
                                                              })}
                                                              {handlers.length > 5 ? (
                                                                  <div style={{ fontSize: 11, fontWeight: 900, color: '#64748b' }}>+{handlers.length - 5} more</div>
                                                              ) : null}
                                                            </div>
                                                        ) : (
                                                            <div style={{ marginTop: 8, fontSize: 11, fontWeight: 800, color: '#94a3b8' }}>No handlers configured.</div>
                                                        )}
                                                      </div>

                                                      <div
                                                          style={{
                                                            borderRadius: 14,
                                                            border: 'none',
                                                            background: 'rgba(2,6,23,0.03)',
                                                            padding: 12,
                                                          }}
                                                      >
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                                                          <div style={{ fontSize: 11, fontWeight: 950, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            <i className="fa-solid fa-user-gear" style={{ color: '#16a34a' }} />
                                                            Agents
                                                          </div>
                                                          <div style={{ fontSize: 11, fontWeight: 900, color: '#64748b' }}>{agents.length}</div>
                                                        </div>
                                                        {agents.length ? (
                                                            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 12 }}>
                                                              {agents.map((a, j) => {
                                                                const t = agentDisplayTitles[j] || `Agent ${j + 1}`;
                                                                const rowKey =
                                                                    (a?.agentId != null && String(a.agentId)) ||
                                                                    (a?.id != null && String(a.id)) ||
                                                                    `${j}-${t}`;
                                                                const tool = sanitizeText(a?.tool || '') || '';
                                                                const outputFormat = sanitizeText(a?.outputFormat || a?.output_format || '') || '';
                                                                const llm = sanitizeText(a?.llm || '') || '';
                                                                const maxRetry = a?.maxRetry != null ? String(a.maxRetry) : '';
                                                                const engines = Array.isArray(a?.engines) ? a.engines : [];
                                                                const humanApproval = a?.humanApproval === true;
                                                                const agentId = sanitizeText(a?.agentId || a?.id || '') || '';
                                                                const goal = sanitizeText(a?.goal || '') || '';
                                                                const role = sanitizeText(a?.role || '') || '';
                                                                const subtitle = [
                                                                  tool ? `Tool: ${tool}` : '',
                                                                  llm ? `LLM: ${llm}` : '',
                                                                  outputFormat ? `Output: ${outputFormat}` : '',
                                                                ]
                                                                    .filter(Boolean)
                                                                    .join(' • ');
                                                                return (
                                                                    <div
                                                                        key={rowKey}
                                                                        style={{
                                                                          borderRadius: 12,
                                                                          border: 'none',
                                                                          background: 'rgba(255,255,255,0.75)',
                                                                          padding: 12,
                                                                          display: 'flex',
                                                                          flexDirection: 'column',
                                                                          gap: 10,
                                                                        }}
                                                                    >
                                                                      <div style={{ fontSize: 12, fontWeight: 900, color: '#0f172a', lineHeight: 1.25 }}>{t}</div>
                                                                      {subtitle ? (
                                                                          <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', lineHeight: 1.45 }}>
                                                                            {subtitle}
                                                                          </div>
                                                                      ) : null}
                                                                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                                        {engines.length ? (
                                                                            <span
                                                                                style={{
                                                                                  height: 20,
                                                                                  padding: '0 8px',
                                                                                  borderRadius: 999,
                                                                                  background: 'rgba(2,132,199,0.10)',
                                                                                  border: '1px solid rgba(2,132,199,0.18)',
                                                                                  color: '#075985',
                                                                                  fontSize: 10,
                                                                                  fontWeight: 900,
                                                                                }}
                                                                                title={engines.map((e) => sanitizeText(String(e)) || String(e)).filter(Boolean).join(', ')}
                                                                            >
                                                      engines: {engines.length}
                                                    </span>
                                                                        ) : null}
                                                                        {maxRetry ? (
                                                                            <span
                                                                                style={{
                                                                                  height: 20,
                                                                                  padding: '0 8px',
                                                                                  borderRadius: 999,
                                                                                  background: 'rgba(245,158,11,0.10)',
                                                                                  border: '1px solid rgba(245,158,11,0.18)',
                                                                                  color: '#92400e',
                                                                                  fontSize: 10,
                                                                                  fontWeight: 900,
                                                                                }}
                                                                                title={`maxRetry: ${maxRetry}`}
                                                                            >
                                                      maxRetry: {maxRetry}
                                                    </span>
                                                                        ) : null}
                                                                        {humanApproval ? (
                                                                            <span
                                                                                style={{
                                                                                  height: 20,
                                                                                  padding: '0 8px',
                                                                                  borderRadius: 999,
                                                                                  background: 'rgba(34,197,94,0.10)',
                                                                                  border: '1px solid rgba(34,197,94,0.18)',
                                                                                  color: '#166534',
                                                                                  fontSize: 10,
                                                                                  fontWeight: 950,
                                                                                  display: 'inline-flex',
                                                                                  alignItems: 'center',
                                                                                  gap: 6,
                                                                                }}
                                                                                title="Human approval enabled"
                                                                            >
                                                      <i className="fa-solid fa-user-check" style={{ fontSize: 10 }} />
                                                      Human approval
                                                    </span>
                                                                        ) : null}
                                                                      </div>
                                                                      {(goal || role) ? (
                                                                          <PublishedHistoryAgentGoalRole goal={goal} role={role} />
                                                                      ) : null}
                                                                    </div>
                                                                );
                                                              })}
                                                            </div>
                                                        ) : (
                                                            <div style={{ marginTop: 8, fontSize: 11, fontWeight: 800, color: '#94a3b8' }}>No agents configured.</div>
                                                        )}
                                                      </div>
                                                    </div>

                                                    {appConfig?._raw ? (
                                                        <div
                                                            style={{
                                                              marginTop: 10,
                                                              borderRadius: 12,
                                                              border: '1px solid rgba(245,158,11,0.22)',
                                                              background: 'rgba(245,158,11,0.08)',
                                                              padding: 10,
                                                              color: '#92400e',
                                                              fontSize: 11,
                                                              fontWeight: 900,
                                                              lineHeight: 1.35,
                                                            }}
                                                            title="App config was not valid JSON; showing raw."
                                                        >
                                                          App config is not valid JSON. Copy App Config will copy the raw string.
                                                        </div>
                                                    ) : null}
                                                  </div>
                                              ) : (
                                                  <div
                                                      style={{
                                                        padding: 24,
                                                        textAlign: 'center',
                                                        color: '#94a3b8',
                                                        fontSize: 12,
                                                        fontWeight: 800,
                                                        lineHeight: 1.5,
                                                        borderRadius: 12,
                                                        border: '1px dashed rgba(15,23,42,0.12)',
                                                        background: 'rgba(248,250,252,0.8)',
                                                      }}
                                                  >
                                                    No app configuration included for this published version.
                                                  </div>
                                              )
                                          ) : null}
                                        </div>
                                    ) : null}
                                  </div>
                              );
                            })
                        )}
                      </div>
                  )}

                  {/* Lazy-load sentinel (IntersectionObserver target) */}
                  <div
                      ref={publishedHistoryLoadMoreRef}
                      style={{
                        height: 1,
                        width: '100%',
                      }}
                  />

                  {/* infinite scroll footer */}
                  {isPublishedHistoryEndVisible && (publishedHistory?.content || []).length > 0 ? (
                      <div
                          style={{
                            padding: '12px 4px',
                            fontSize: 12,
                            fontWeight: 800,
                            color: publishedHistoryLoading ? '#94a3b8' : publishedHistoryHasMore ? '#cbd5e1' : '#94a3b8',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8,
                          }}
                      >
                        {publishedHistoryLoading ? (
                            <>
                              <i className="fa-solid fa-spinner" style={{ fontSize: 12, animation: 'sa-spin 1s linear infinite' }} />
                              Loading more…
                            </>
                        ) : publishedHistoryHasMore ? (
                            <>Scroll to load more</>
                        ) : (
                            <>You’re all caught up</>
                        )}
                      </div>
                  ) : null}
                </div>
              </div>
            </div>
        ) : null}

        <EvaluateModal
            isOpen={isEvaluateOpen}
            onClose={() => setIsEvaluateOpen(false)}
            agentNodes={agentNodes}
            projectId={projectId}
            orchestratorId={orchestratorId}
        />

        <SAModalConfirm
            show={isStopConfirmOpen}
            title="Stop workflow"
            message="Stop the running workflow?"
            okText="Stop"
            cancelText="Cancel"
            handleOk={() => {
              setIsStopConfirmOpen(false);
              onExecute?.();
            }}
            handleCancel={() => setIsStopConfirmOpen(false)}
        />

        {/* Restore default confirmation */}
        <SAModalConfirm
            show={isRestoreConfirmOpen}
            centered={false}
            zIndex={2100}
            title="Restore template"
            message={
              <span style={{ display: 'inline-block', textAlign: 'left', fontSize: 14 }}>
            <span style={{ display: 'block' }}>
              Are you sure you want to restore{' '}
              <code> {restoreConfirmData?.title || 'this version'} </code>
              {' '}as the default template?
            </span>

          </span>
            }
            okText={restoringPublishedId ? 'Restoring...' : 'Restore'}
            cancelText="Cancel"
            handleOk={async () => {
              const id = restoreConfirmData?.id;
              setIsRestoreConfirmOpen(false);
              setIsPublishedHistoryOpen(false);
              setRestoreConfirmData(null);
              if (!id) return;
              await restorePublishedAsDefault(id);
            }}
            handleCancel={() => {
              setIsRestoreConfirmOpen(false);
              setRestoreConfirmData(null);
            }}
        />

        {/* Publish confirmation */}
        {isPublishConfirmOpen ? (
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Publish workflow confirmation"
                style={{
                  position: 'fixed',
                  inset: 0,
                  zIndex: 2000,
                  background: 'rgba(15, 23, 42, 0.55)',
                  display: 'flex',
                  alignItems: 'stretch',
                  justifyContent: 'flex-end',
                }}
            >
              <div
                  role="presentation"
                  onMouseDown={() => setIsPublishConfirmOpen(false)}
                  style={{
                    position: 'absolute',
                    inset: 0,
                  }}
              >
                <div
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{
                      position: 'relative',
                      height: '100%',
                      width: '100%',
                      maxWidth: 460,
                      marginLeft: 'auto',
                      background: '#ffffff',
                      borderLeft: '1px solid rgba(15,23,42,0.10)',
                      boxShadow: '-24px 0 60px rgba(15,23,42,0.22)',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                >
                  <div
                      style={{
                        padding: 16,
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 12,
                        borderBottom: '1px solid rgba(15,23,42,0.08)',
                        background: 'linear-gradient(180deg, rgba(56,189,248,0.12), rgba(99,102,241,0.06))',
                      }}
                  >
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0 }}>
                      <div
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 14,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(56,189,248,0.14)',
                            border: '1px solid rgba(56,189,248,0.24)',
                            flexShrink: 0,
                          }}
                      >
                        <i className="fa-solid fa-cloud-arrow-up" style={{ color: '#0284c7', fontSize: 16 }} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 900, color: '#0f172a', lineHeight: 1.2 }}>
                          Publish workflow
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 3, lineHeight: 1.25 }}>
                          Add a name/description and publish the latest configuration.
                        </div>
                      </div>
                    </div>

                    <button
                        type="button"
                        onClick={() => setIsPublishConfirmOpen(false)}
                        aria-label="Close publish drawer"
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: 10,
                          border: '1px solid rgba(15,23,42,0.12)',
                          background: '#ffffff',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#475569',
                          flexShrink: 0,
                        }}
                    >
                      <i className="fa-solid fa-xmark" style={{ fontSize: 14, color: '#64748b' }} />
                    </button>
                  </div>

                  <div style={{ padding: 16, overflowY: 'auto', flex: '1 1 auto' }}>

                    <div style={{ marginTop: 14 }}>
                      <div className="font14 required" style={{ fontSize: 12, fontWeight: 900, color: '#334155', marginBottom: 6 }}>Name</div>
                      <input
                          value={publishName}
                          onChange={(e) => {
                            setPublishName(e.target.value);
                            if (publishError) setPublishError(null);
                          }}
                          placeholder="Workflow name"
                          autoFocus
                          style={{
                            width: '100%',
                            height: 36,
                            borderRadius: 12,
                            border: publishError ? '1px solid rgba(239,68,68,0.55)' : '1px solid rgba(15,23,42,0.12)',
                            padding: '0 12px',
                            fontSize: 13,
                            outline: 'none',
                            boxShadow: publishError ? '0 0 0 3px rgba(239,68,68,0.12)' : '0 0 0 0 rgba(0,0,0,0)',
                            transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
                          }}
                      />
                    </div>

                    <div style={{ marginTop: 14 }}>
                      <div style={{ fontSize: 12, fontWeight: 900, color: '#334155', marginBottom: 6 }}>Description</div>
                      <textarea
                          value={publishDescription}
                          onChange={(e) => setPublishDescription(e.target.value)}
                          placeholder="What does this workflow do?"
                          rows={4}
                          style={{
                            width: '100%',
                            borderRadius: 12,
                            border: '1px solid rgba(15,23,42,0.12)',
                            padding: '10px 12px',
                            fontSize: 13,
                            outline: 'none',
                            resize: 'vertical',
                          }}
                      />
                    </div>

                    {publishError ? (
                        <div
                            style={{
                              marginTop: 12,
                              padding: '10px 12px',
                              borderRadius: 12,
                              background: 'rgba(239,68,68,0.08)',
                              border: '1px solid rgba(239,68,68,0.20)',
                              fontSize: 12,
                              fontWeight: 900,
                              color: '#b91c1c',
                            }}
                        >
                          {publishError}
                        </div>
                    ) : null}
                  </div>

                  <div
                      style={{
                        padding: 16,
                        borderTop: '1px solid rgba(15,23,42,0.08)',
                        background: 'rgba(248,250,252,0.85)',
                        backdropFilter: 'blur(8px)',
                        display: 'flex',
                        gap: 10,
                        justifyContent: 'flex-end',
                      }}
                  >
                    <button
                        type="button"
                        onClick={() => setIsPublishConfirmOpen(false)}
                        style={{
                          height: 36,
                          padding: '0 12px',
                          borderRadius: 12,
                          border: '1px solid rgba(15,23,42,0.12)',
                          background: '#ffffff',
                          color: '#334155',
                          fontSize: 13,
                          fontWeight: 800,
                          cursor: 'pointer',
                        }}
                    >
                      Cancel
                    </button>
                    <div className="sa-publish-confirm-split" style={{ position: 'relative', display: 'inline-flex', alignItems: 'stretch' }}>
                      <button
                          type="button"
                          onClick={handlePublishConfirm}
                          style={{
                            height: 36,
                            padding: '0 14px',
                            borderTopLeftRadius: 12,
                            borderBottomLeftRadius: 12,
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0,
                            border: '1px solid rgba(56,189,248,0.80)',
                            background: '#ffffff',
                            color: '#0284c7',
                            fontSize: 13,
                            fontWeight: 900,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                      >
                        <i className="fa-solid fa-cloud-arrow-up" style={{ color: '#0284c7', fontSize: 16 }} />
                        <span>Publish</span>
                      </button>

                      <button
                          type="button"
                          onClick={() => setIsPublishConfirmSplitMenuOpen((v) => !v)}
                          aria-haspopup="menu"
                          aria-expanded={isPublishConfirmSplitMenuOpen ? 'true' : 'false'}
                          style={{
                            height: 36,
                            width: 40,
                            padding: 0,
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                            borderTopRightRadius: 12,
                            borderBottomRightRadius: 12,
                            border: '1px solid rgba(56,189,248,0.80)',
                            borderLeft: 'none',
                            background: '#ffffff',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          title="More options"
                      >
                        <i className="fa-solid fa-caret-down" style={{ color: '#0284c7', fontSize: 14 }} />
                      </button>

                      {isPublishConfirmSplitMenuOpen ? (
                          <div
                              role="menu"
                              style={{
                                position: 'absolute',
                                right: 0,
                                bottom: 'calc(100% + 8px)',
                                minWidth: 200,
                                padding: 8,
                                borderRadius: 12,
                                background: '#ffffff',
                                border: '1px solid rgba(15,23,42,0.10)',
                                boxShadow: '0 12px 28px rgba(15,23,42,0.14)',
                                zIndex: 2105,
                              }}
                          >
                            <button
                                role="menuitem"
                                type="button"
                                onClick={async () => {
                                  setIsPublishConfirmSplitMenuOpen(false);
                                  await handleSaveAndPublishConfirm();
                                }}
                                style={{
                                  width: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 10,
                                  padding: '8px 10px',
                                  borderRadius: 10,
                                  border: '1px solid transparent',
                                  background: 'transparent',
                                  cursor: 'pointer',
                                  color: '#334155',
                                  fontSize: 13,
                                  fontWeight: 800,
                                  textAlign: 'left',
                                }}
                            >
                              <i className="fa-solid fa-floppy-disk" style={{ fontSize: 12, color: '#3b82f6' }} />
                              Save &amp; Publish
                            </button>
                          </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>
        ) : null}
      </div>
  );
};

export default React.memo(WorkflowHeader);
export { Alert, getDisplayStatus };